import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import React from 'react';
import { useTranslation } from 'components/i18n/Translator';
import clsx from 'clsx';
import MarkdownIncidentComm from '@/components/MarkdownIncidentComm';
import { FileSpec, useConfig } from '@chainlit/react-client';
import Page from 'pages/Page';
import { RotateCcw, X, Search, Mail, Check, Eye, Pencil, XCircle } from 'lucide-react';
import {
  buildIncidentEmailHTML,
} from '@/components/IncidentUpdates/EmailGeneration';

import CircumventedDialog from '@/components/IncidentUpdates/CircumventedDialog';

import { sendIncidentEmail, saveImpactedApplications, generateLLMUpdates, fetchIncidentUpdates, addIncidentUpdate, deleteIncidentUpdate } from '@/api';
import {
  fetchMergedIncidents,
  fetchIncidentDetails,
  reopenIncidentUpdates,
  updateIncidentMeta,
  fetchExecutiveSummary,
  saveEmailRecipientsOnly
} from '@/api/fetchIncidents';
import EmailIframePreview from '@/components/IncidentUpdates/EmailIframePreview';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { useAuth } from '@chainlit/react-client';
import { fetchBatApplications, ApplicationItem, fetchApplicationDlsMulti } from '@/api/fetchApplications';
import EmailSummary from '@/components/IncidentUpdates/EmailSummary';
import KnowledgeArticle from '@/components/IncidentUpdates/KnowledgeArticle';
import { setApprovalStatus } from '../api/approvals';
import { approveIncidentUpdate, editIncidentUpdate, updateIncidentState } from '../api/fetchIncidents';
import { useLocation, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Alert } from '@/components/Alert';

interface Incident {
  id: string;
  title: string;
  severity: 'P4' | 'P3' | 'P2' | 'P1';
  updatedAt: string;
  state: string;
  configuration_class: string;
  assigned_to: string;
  owner?: string;
  priority?: string;
  description?: string;
  businessImpact?: string;
  bridgeDetails?: string;
  businessLine?: string;
  startTime?: string;
  endTime?: string;
  problemRecord?: string;
  circumvented?: boolean | null; // NEW flag from API (true = closed, false/null = open)
  ka_url?: string;              // NEW
  ka_generated_at?: string;     // NEW
  email_subject?: string;       // NEW
  circumvention_time?: string;  // NEW: CST timestamp
}

interface Communication {
  id: string;
  incidentId: string;
  author: string;
  role: string;
  timestamp: string;
  content: string;
  update_id?: number;
  iscircumvented?: boolean;
  iscommunication_sent?: boolean;
  approved?: boolean;          
  approval_id?: number;
}

interface EmailRecipient {
  email: string;
  appNumber?: string | number | null;
}

/* Utility */
const timeAgo = (iso: string) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60000); // minutes
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};


// Convert timestamps (e.g. "2025-10-07 19:10:31") to Central time and always show CDT label
const formatDateTime = (raw: string) => {
  if (!raw) return '-';
  const s = raw.trim();
  let d: Date | null = null;

  // Match "YYYY-MM-DD HH:MM:SS" (no timezone -> treat as UTC)
  const m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/
  );
  if (m) {
    const [, Y, Mo, Da, H, Mi, S] = m;
    d = new Date(Date.UTC(+Y, +Mo - 1, +Da, +H, +Mi, +S));
  } else {
    const parsed = new Date(s);
    if (!Number.isNaN(parsed.getTime())) d = parsed;
  }

  if (!d) return '-';

  // Format in America/Chicago (Central) and force suffix "CDT"
  const base = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'America/Chicago'
  }).format(d);

  return `${base} CDT`;
};

export default function IncidentUpdatesPage() {
  const { t } = useTranslation();
  const { config } = useConfig();

  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loadingIncidents, setLoadingIncidents] = useState(false);
  const [communications, _setCommunications] = useState<Communication[]>([]);

  // ✅ Wrapped setter that deduplicates by update_id
  const setCommunications = useCallback((next: Communication[] | ((prev: Communication[]) => Communication[])) => {
    _setCommunications(prev => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      
      // ✅ Deduplicate by update_id
      const seen = new Set<number>();
      const deduplicated = resolved.filter(c => {
        if (c.update_id == null) return true; // keep non-update items (e.g., temp local items)
        if (seen.has(c.update_id)) {
          return false; // skip duplicate
        }
        seen.add(c.update_id);
        return true;
      });
      
      return deduplicated;
    });
  }, []);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [stateFilter, setStateFilter] = useState<string>(''); // NEW: filter by state
  const [circumventedFilter, setCircumventedFilter] = useState<'all' | 'yes' | 'no'>('all'); // NEW
  const [summary, setSummary] = useState<string>('Select an incident to see a generated summary.');
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [rephrasedDraft, setRephrasedDraft] = useState('');
  const [rephrasing, setRephrasing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const [impactedApps, setImpactedApps] = useState<Record<string, string[]>>({});
  const [appSearch, setAppSearch] = useState('');
  const appInputRef = useRef<HTMLInputElement | null>(null);

  const [savingImpacted, setSavingImpacted] = useState(false);
  const [impactedDirty, setImpactedDirty] = useState<Record<string, boolean>>({});
  const [editingRightApps, setEditingRightApps] = useState(false);
  const lastSavedAppsRef = useRef<Record<string, string[]>>({}); // snapshot of last persisted
  const [impactedAppsPersisted, setImpactedAppsPersisted] = useState<Record<string, boolean>>({});

  const hasRecipientEmail = useCallback(
    (list: EmailRecipient[], email: string) =>
      list.some(r => r.email.toLowerCase() === email.toLowerCase()),
    []
  );

  // NEW: edit incident meta (description / impact / bridge / problem record / RCA owner)
  const [editingIncidentMeta, setEditingIncidentMeta] = useState(false);
  const [incidentMetaDraft, setIncidentMetaDraft] = useState<{
    description: string;
    businessImpact: string;
    bridgeDetails: string;
    problemRecord: string;
    rcaOwner: string;
    circumventionTime: string; // NEW
  }>({
    description: '',
    businessImpact: '',
    bridgeDetails: '',
    problemRecord: '',
    rcaOwner: '',
    circumventionTime: ''
  });
  const [savingIncidentMeta, setSavingIncidentMeta] = useState(false);
  const [incidentMetaDirty, setIncidentMetaDirty] = useState(false);

  // Safe derived flag for currently selected incident
  const dirtyForSelected = useMemo(
    () => (selectedId ? !!impactedDirty[selectedId] : false),
    [selectedId, impactedDirty]
  );

  const hasPersistedApps = useMemo(
    () => (selectedId ? !!impactedAppsPersisted[selectedId] : false),
    [selectedId, impactedAppsPersisted]
  );

  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailRecipientsMap, setEmailRecipientsMap] = useState<Record<string, EmailRecipient[]>>({});
  const [emailRecipients, _setEmailRecipients] = useState<EmailRecipient[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailHtml, setEmailHtml] = useState<string>('');
  const [execSummary, setExecSummary] = useState('');
  const [loadingExecSummary, setLoadingExecSummary] = useState(false);

  const [middleView, setMiddleView] = useState<'updates' | 'worknotes'>('updates');
  const [previewOpen, setPreviewOpen] = useState(false);

  const [detailLoadingId, setDetailLoadingId] = useState<string | null>(null);
  const detailLoadedRef = useRef<Set<string>>(new Set());

  const [updatesLoadingId, setUpdatesLoadingId] = useState<string | null>(null);
  const updatesLoadedRef = useRef<Set<string>>(new Set());
  const [updatesRefreshing, setUpdatesRefreshing] = useState(false); // <-- added

  const [closedUpdates, setClosedUpdates] = useState<Set<string>>(new Set());
  const isUpdatesClosed = selectedId ? closedUpdates.has(selectedId) : false;

  const [closingDialogOpen, setClosingDialogOpen] = useState(false);
  const [closingStep, setClosingStep] = useState<'confirm' | 'notes' | 'preview' | 'done'>('confirm');
  const [closingNotes, setClosingNotes] = useState('');
  const [closingRephrased, setClosingRephrased] = useState('');
  const [closingRephrasing, setClosingRephrasing] = useState(false);
  const [closingEmailSending, setClosingEmailSending] = useState(false);
  const [closurePreviewOpen, setClosurePreviewOpen] = useState(false);
  const [closureEmailHtml, setClosureEmailHtml] = useState('');

  const [knowledgeArticleOpen, setKnowledgeArticleOpen] = useState(false);

  const [appCatalog, setAppCatalog] = useState<ApplicationItem[]>([]);
  const [loadingApps, setLoadingApps] = useState(false);

  const initialFetchDoneRef = useRef(false);
  const refreshInFlightRef = useRef<Promise<void> | null>(null);


  const WS_URL =
  (import.meta as any).env.VITE_NOTIFS_WS_URL ||
  (import.meta.env.MODE === 'production'
    ? 'wss://askawatch.cbre.com/api/updates/notifications'
    : import.meta.env.MODE === 'staging'
    ? 'wss://askawatch-test.cbre.com/api/updates/notifications'
    : 'ws://localhost:8000/api/updates/notifications');

  type UpdateEvent =
  | { type: 'update:approved'; incidentId?: string; update_id: number; approved?: boolean; iscommunication_sent?: boolean; executive_summary?: string }
  | { type: 'update:edited'; incidentId?: string; update_id: number; content: string; executive_summary?: string }
  | { type: 'update:deleted'; incidentId?: string; update_id: number }
  | { type: 'update:inserted'; incidentId: string; comm: Communication }
  | { type: 'impacted_apps:updated'; incidentId: string; apps: string[] }
  | { type: 'incident:circumvented'; incidentId: string }
  | { type: 'incident:state_changed'; incidentId: string; state: string }; // ✅ NEW event type


  useEffect(() => {
    const ws = new WebSocket(WS_URL);

    ws.onopen = () => {
      // subscribe to current incident topic
      if (selectedId) {
        ws.send(JSON.stringify({ action: 'subscribe', topic: `incident:${selectedId}` }));
      }
    };

    ws.onmessage = (evt) => {
      let msg: UpdateEvent | null = null;
      try { msg = JSON.parse(evt.data); } catch { return; }
      if (!msg) return;

      // If message is incident-scoped and we're looking at a different one, ignore
      if ('incidentId' in msg && msg.incidentId && selectedId && msg.incidentId !== selectedId) return;

      switch (msg.type) {
        case 'update:approved':
          setCommunications(prev =>
            prev.map(x =>
              x.update_id === msg.update_id
                ? { ...x, approved: msg.approved ?? true, iscommunication_sent: msg.iscommunication_sent ?? true }
                : x
            )
          );
          if ('executive_summary' in msg && msg.executive_summary) {
            setExecSummary(msg.executive_summary);
          }
          regenSummary();
          break;
        case 'update:edited':
          setCommunications(prev =>
            prev.map(x =>
              x.update_id === msg.update_id
                ? { ...x, content: msg.content, approved: true }
                : x
            )
          );
          // ✅ Update executive summary if provided
          if ('executive_summary' in msg && msg.executive_summary) {
            setExecSummary(msg.executive_summary);
          }
          regenSummary();
          break;
        case 'update:deleted':
          setCommunications(prev => prev.filter(x => x.update_id !== msg.update_id));
          regenSummary();
          break;
        case 'update:inserted':
          setCommunications(prev => {
            const exists = prev.some(c => c.update_id === msg.comm?.update_id);

            if (exists) {
              return prev; 
            }
            return [...prev, msg.comm];
          });
          regenSummary();
          break;
        // ✅ NEW: Handle impacted apps updates from other users
        case 'impacted_apps:updated':
          if (msg.incidentId && msg.apps) {
            setImpactedApps(prev => ({
              ...prev,
              [msg.incidentId]: msg.apps
            }));
            lastSavedAppsRef.current[msg.incidentId] = [...msg.apps];
            setImpactedDirty(d => ({ ...d, [msg.incidentId]: false }));
            setImpactedAppsPersisted(p => ({ ...p, [msg.incidentId]: !!msg.apps.length }));

            if (msg.apps.length && msg.incidentId === selectedId) {
              const numbers = msg.apps
                .map(n => appNumberLookup[n])
                .filter(n => n != null);
              
              if (numbers.length) {
                fetchApplicationDlsMulti(numbers)
                  .then(dlMap => {
                    const collected: EmailRecipient[] = [];
                    Object.entries(dlMap).forEach(([num, raw]) => {
                      raw
                        .split(/[,;]+/)
                        .map(s => s.trim())
                        .filter(Boolean)
                        .forEach(addr => {
                          if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) {
                            const existing = emailRecipientsMap[msg.incidentId] || [];
                            if (!hasRecipientEmail(existing, addr) &&
                                !hasRecipientEmail(collected, addr)) {
                              collected.push({ email: addr, appNumber: num });
                            }
                          }
                        });
                    });
                    
                    if (collected.length) {
                      setEmailRecipientsMap(prev => {
                        const existing = prev[msg.incidentId] || [];
                        const merged = [
                          ...existing,
                          ...collected.filter(r => !hasRecipientEmail(existing, r.email))
                        ];
                        if (selectedId === msg.incidentId) {
                          _setEmailRecipients(merged);
                        }
                        return { ...prev, [msg.incidentId]: merged };
                      });
                    }
                  })
                  .catch(e => {
                    console.warn('Bulk DL fetch failed via WebSocket', e);
                  });
              }
            }
          }
          break;
        // ✅ NEW: Handle incident circumvented event
        case 'incident:circumvented':
          if (msg.incidentId) {
            setClosedUpdates(prev => new Set([...prev, msg.incidentId]));
            setIncidents(prev => prev.map(i =>
              i.id === msg.incidentId 
                ? { ...i, circumvented: true, state: 'circumvented' } 
                : i
            ));
          }
          break;
        
        case 'incident:state_changed':
          if (msg.incidentId) {
            setIncidents(prev => prev.map(i =>
              i.id === msg.incidentId 
                ? { ...i, state: msg.state } 
                : i
            ));
          }
          break;
        default:
          break;
      }
    };

    ws.onclose = () => { /* optional reconnect logic */ };

    return () => {
      try {
        if (selectedId) {
          ws.send(JSON.stringify({ action: 'unsubscribe', topic: `incident:${selectedId}` }));
        }
        ws.close();
      } catch {}
    };
  }, [selectedId]);

  const loadApps = useCallback(async () => {
    setLoadingApps(true);
    try {
      const apps = await fetchBatApplications();
      setAppCatalog(apps);
    } catch (e) {
      console.error('Failed to load applications', e);
      if (appCatalog.length === 0) {
        setAppCatalog([{ name: 'auth-service', number: 'auth-service' }]);
      }
    } finally {
      setLoadingApps(false);
    }
  }, [appCatalog]);

  useEffect(() => {
    loadApps();
  }, []); // <-- only runs once on mount

  const currentImpactedApps = selectedId
    ? impactedApps[selectedId] || []
    : [];

  const filteredApps = useMemo(() => {
    const currentNames = currentImpactedApps;
    const base = appCatalog.filter(a => !currentNames.includes(a.name));
    if (!appSearch.trim()) return base.slice(0, 6);
    return base
      .filter(a => a.name.toLowerCase().includes(appSearch.toLowerCase()))
      .slice(0, 10);
  }, [appSearch, appCatalog, currentImpactedApps]);

  const addImpactedApp = (app: string) => {
    if (!selectedId || currentImpactedApps.includes(app)) return;
    setImpactedApps((prev) => {
      const next = {
        ...prev,
        [selectedId]: [...(prev[selectedId] || []), app]
      };
      return next;
    });
    if (selectedId) {
      setImpactedDirty((d) => ({ ...d, [selectedId]: true }));
    }
    setAppSearch('');
    appInputRef.current?.focus();
  };

  const removeImpactedApp = (app: string) => {
    if (!selectedId) return;
    setImpactedApps((prev) => {
      const next = {
        ...prev,
        [selectedId]: (prev[selectedId] || []).filter(
          (x) => x !== app
        )
      };
      return next;
    });
    if (selectedId) {
      setImpactedDirty((d) => ({ ...d, [selectedId]: true }));
    }
  };

  const appNumberLookup = useMemo(
    () => Object.fromEntries(appCatalog.map(a => [a.name, a.number])),
    [appCatalog]
  );

  const persistImpactedApps = useCallback(async (incidentId: string) => {
    if (!incidentId) return;
    const names = impactedApps[incidentId] || [];
    setSavingImpacted(true);
    
    try {
      let recipientsToSave: EmailRecipient[] = [];
      
      if (names.length > 0) {
        const numbers = names
          .map(n => appNumberLookup[n])
          .filter(n => n != null);
        
        if (numbers.length > 0) {
          console.log('Fetching DLs for apps before saving:', numbers);
          try {
            const dlMap = await fetchApplicationDlsMulti(numbers);
            const collected: EmailRecipient[] = [];
            
            Object.entries(dlMap).forEach(([num, raw]) => {
              raw
                .split(/[,;]+/)
                .map(s => s.trim())
                .filter(Boolean)
                .forEach(addr => {
                  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) {
                    const existing = emailRecipientsMap[incidentId] || [];
                    if (!hasRecipientEmail(existing, addr) &&
                        !hasRecipientEmail(collected, addr)) {
                      collected.push({ email: addr, appNumber: num });
                    }
                  }
                });
            });
            
            const existingManual = emailRecipientsMap[incidentId] || [];
            recipientsToSave = [
              ...existingManual,
              ...collected.filter(r => !hasRecipientEmail(existingManual, r.email))
            ];
            
            console.log('Recipients to save:', recipientsToSave);
          } catch (e) {
            console.warn('Bulk DL fetch failed before save:', e);
            recipientsToSave = emailRecipientsMap[incidentId] || [];
          }
        } else {
          recipientsToSave = emailRecipientsMap[incidentId] || [];
        }
      } else {
        // ✅ No apps = clear recipients
        recipientsToSave = [];
      }
      
      console.log('Persisting impacted apps with recipients', { 
        incidentId, 
        names, 
        recipientsCount: recipientsToSave.length,
        recipients: recipientsToSave 
      });
      
      const saveResp = await saveImpactedApplications(
        incidentId,
        names,
        names.map(n => [n, appNumberLookup[n] ?? null]),
        recipientsToSave
      );
      
      console.log('Save response:', saveResp);
      
      lastSavedAppsRef.current[incidentId] = [...names];
      setImpactedDirty(d => ({ ...d, [incidentId]: false }));
      setImpactedAppsPersisted(p => ({ ...p, [incidentId]: !!names.length }));
      setEditingRightApps(false);
      
      // ✅ Mark DL as loaded (prevents re-fetch on next load)
      dlLoadedRef.current.add(incidentId);
      
      if (recipientsToSave.length > 0) {
        setEmailRecipientsMap(prev => ({
          ...prev,
          [incidentId]: recipientsToSave
        }));
        
        if (selectedId === incidentId) {
          _setEmailRecipients(recipientsToSave);
        }
      } else {
        setEmailRecipientsMap(prev => ({
          ...prev,
          [incidentId]: [] // ✅ Explicitly save empty array
        }));
        
        if (selectedId === incidentId) {
          _setEmailRecipients([]);
        }
      }
      
    } catch (e) {
      console.error('Failed saving impacted apps with recipients:', e);
    } finally {
      setSavingImpacted(false);
    }
  }, [impactedApps, appNumberLookup, emailRecipientsMap, selectedId, hasRecipientEmail]);

  useEffect(() => {
    if (selectedId && lastSavedAppsRef.current[selectedId] == null) {
      lastSavedAppsRef.current[selectedId] = [...(impactedApps[selectedId] || [])];
    }
  }, [selectedId, impactedApps]);

  // Example fileSpec (if later you embed a composer)
  const fileSpec: FileSpec = useMemo(
    () => ({
      max_size_mb: config?.features?.spontaneous_file_upload?.max_size_mb || 100,
      max_files: 10,
      accept: { 'application/*': [], 'image/*': [], 'text/*': [] }
    }),
    [config]
  );

  const location = useLocation();
  const navigate = useNavigate();

  // NEW: capture URL incident/update on mount before first refresh
  const initialIncidentIdRef = useRef<string | null>(null);
  const initialUpdateIdRef = useRef<number | null>(null);

  useEffect(() => {
    // Run once on mount to seed selection from URL
    const sp = new URLSearchParams(location.search);
    const incidentParam = sp.get('incident');
    const updateParam = sp.get('update');

    if (incidentParam) {
      initialIncidentIdRef.current = incidentParam;
      setSelectedId(incidentParam); // set before refreshIncidents
    }
    initialUpdateIdRef.current = updateParam ? Number(updateParam) : null;
    // no deps -> only run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshIncidents = useCallback(async () => {
    if (refreshInFlightRef.current) return refreshInFlightRef.current;
    const run = (async () => {
      setLoadingIncidents(true);
      try {
        const { incidents: merged } = await fetchMergedIncidents();
        const sorted = [...merged].sort(
          (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
        setIncidents(sorted);

        setClosedUpdates(new Set(sorted.filter(i => (i as Incident).circumvented === true).map(i => i.id)));

        // Only auto-select if nothing is selected
        if (!selectedId && sorted.length) {
          const sp = new URLSearchParams(location.search);
          const fromUrl = sp.get('incident');
          setSelectedId(fromUrl && sorted.some(i => i.id === fromUrl) ? fromUrl : sorted[0].id);
        }
      } finally {
        setLoadingIncidents(false);
        refreshInFlightRef.current = null;
      }
    })();
    refreshInFlightRef.current = run;
    return run;
  }, [selectedId, location.search]);

  useEffect(() => {
    // Guard first effect run (StrictMode mounts twice in dev)
    if (initialFetchDoneRef.current) return;
    initialFetchDoneRef.current = true;
    refreshIncidents();
  }, [refreshIncidents]);

  useEffect(() => {
    if (!selectedId) return;
    if (detailLoadedRef.current.has(selectedId)) return;

    let active = true;
    (async () => {
      setDetailLoadingId(selectedId);
      try {
        const { incident: detailed, communications: comms, impactedApps: fromDB, emailRecipients: recipientsFromDB } =
          await fetchIncidentDetails(selectedId);

        if (!active) return;

        if (detailed) {
          setIncidents(prev =>
            prev.map(i => {
              if (i.id !== detailed.id) return i;
              const keepUpdatedAt =
                new Date(detailed.updatedAt).getTime() < new Date(i.updatedAt).getTime()
                  ? i.updatedAt
                  : detailed.updatedAt;
              return { ...i, ...detailed, updatedAt: keepUpdatedAt };
            })
          );
        }

        if (comms.length) {
          setCommunications(prev => {
            const existingIds = new Set(prev.map(c => c.id));
            return [...prev, ...comms.filter(c => !existingIds.has(c.id))];
          });
        }

        // ✅ FIXED: Always load saved email recipients first (even if empty array)
        console.log('Loading saved recipients from DB:', recipientsFromDB);
        const savedRecipients = recipientsFromDB || [];
        
        setEmailRecipientsMap(prev => ({
          ...prev,
          [selectedId]: savedRecipients
        }));
        
        if (selectedId === selectedIdRef.current) {
          _setEmailRecipients(savedRecipients);
        }

        // Handle impacted applications from DB
        if (fromDB !== undefined) {
          if (fromDB && fromDB.length) {
            setImpactedApps(prev => ({
              ...prev,
              [selectedId]: fromDB
            }));
            lastSavedAppsRef.current[selectedId] = [...fromDB];
            setImpactedDirty(d => ({ ...d, [selectedId]: false }));
            setImpactedAppsPersisted(p => ({ ...p, [selectedId]: true }));

            // ✅ FIXED: Only fetch DLs if we've NEVER fetched them before AND no saved recipients exist
            const hasNeverFetchedDLs = !dlLoadedRef.current.has(selectedId);
            const hasNoSavedRecipients = !savedRecipients.length;
            
            if (hasNeverFetchedDLs && hasNoSavedRecipients) {
              console.log('No saved recipients and never fetched DLs - fetching now for apps:', fromDB);
              const numbers = fromDB
                .map(n => appNumberLookup[n])
                .filter(n => n != null);
                
              if (numbers.length) {
                try {
                  const dlMap = await fetchApplicationDlsMulti(numbers);
                  const toAdd: EmailRecipient[] = [];
                  
                  Object.entries(dlMap).forEach(([num, raw]) => {
                    raw
                      .split(/[,;]+/)
                      .map(s => s.trim())
                      .filter(Boolean)
                      .forEach(addr => {
                        const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr);
                        if (valid && !toAdd.some(r => r.email === addr)) {
                          toAdd.push({ email: addr, appNumber: num });
                        }
                      });
                  });
                  
                  if (toAdd.length) {
                    console.log('Adding DL recipients for first time:', toAdd);
                    setEmailRecipientsMap(prev => ({
                      ...prev,
                      [selectedId]: toAdd
                    }));
                    
                    if (selectedId === selectedIdRef.current) {
                      _setEmailRecipients(toAdd);
                    }
                  }
                } catch (e) {
                  console.warn('Auto DL fetch failed on first load', e);
                } finally {
                  dlLoadedRef.current.add(selectedId);
                }
              } else {
                dlLoadedRef.current.add(selectedId);
              }
            } else if (hasNoSavedRecipients) {
              // ✅ Has saved empty recipients list (user cleared all emails) - don't fetch DLs
              console.log('User has explicitly saved empty recipients list - skipping DL fetch');
              dlLoadedRef.current.add(selectedId);
            } else {
              // ✅ Has saved recipients - use them, don't fetch DLs
              console.log('Using saved recipients, skipping DL fetch');
              dlLoadedRef.current.add(selectedId);
            }
          } else {
            // ✅ FIXED: Add missing else block with proper closing
            // No apps saved
            setImpactedApps(prev => ({
              ...prev,
              [selectedId]: prev[selectedId] || []
            }));
            setImpactedAppsPersisted(p => ({ ...p, [selectedId]: false }));
          }
        } // ✅ FIXED: This closes the `if (fromDB !== undefined)` block

        detailLoadedRef.current.add(selectedId);
      } catch (e) {
        console.error('Failed to load incident detail', selectedId, e);
      } finally {
        if (active) {
          setDetailLoadingId(prev => (prev === selectedId ? null : prev));
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [selectedId, appNumberLookup]); // ✅ REMOVED emailRecipients from deps

  const loadIncidentUpdates = useCallback(
    async (incidentId: string, force = false) => {
      if (!incidentId || incidentId === 'INC-DUMMY') return;
      if (!force && updatesLoadedRef.current.has(incidentId)) return;
      setUpdatesLoadingId(incidentId);
      try {
        if (force) setUpdatesRefreshing(true);
        const resp = await fetchIncidentUpdates(incidentId);
        if (resp.success) {
          setCommunications(prev => {
            const existingIds = new Set(prev.map(c => c.id));
            const mapped: Communication[] = resp.updates.map(u => ({
              id: `upd-${u.update_id}`,
              incidentId,
              author: u.updated_by || 'analyst',
              role: 'analyst',
              timestamp: u.timestamp || new Date().toISOString(),
              content: u.update,
              update_id: u.update_id,
              iscircumvented: u.circumvented || false,
              iscommunication_sent: u.communication_sent || false,
              approved: u.approved || false,           // NEW
              approval_id: u.approval_id ?? undefined // NEW
            }));
            return [
              ...prev,
              ...mapped.filter(m => !existingIds.has(m.id))
            ];
          });
          updatesLoadedRef.current.add(incidentId);
        }
      } catch (e) {
        console.error('Failed fetching incident updates', incidentId, e);
      } finally {
        setUpdatesLoadingId(prev => (prev === incidentId ? null : prev));
        setUpdatesRefreshing(false);
      }
    },
    []
  );

  const refreshSelectedUpdates = useCallback(() => {
    if (!selectedId) return;
    // allow re-fetch by removing cache marker then forcing
    updatesLoadedRef.current.delete(selectedId);
    loadIncidentUpdates(selectedId, true);
  }, [selectedId, loadIncidentUpdates]);

  const filteredIncidents = useMemo(() => {
    return incidents.filter((i) => {
      // text filter on id/title
      const textOk =
        i.id.toLowerCase().includes(filter.toLowerCase()) ||
        i.title.toLowerCase().includes(filter.toLowerCase());

      // state filter
      const stateOk = stateFilter
        ? (i.state || '').toLowerCase() === stateFilter.toLowerCase()
        : true;

      // circumvented filter (boolean or null)
      const cFlag = i.circumvented === true ? 'yes' : 'no';
      const circOk =
        circumventedFilter === 'all' ? true : cFlag === circumventedFilter;

      return textOk && stateOk && circOk;
    });
  }, [incidents, filter, stateFilter, circumventedFilter]);

  const currentComms = useMemo(
    () => communications.filter((c) => c.incidentId === selectedId),
    [communications, selectedId]
  );

  // Split comms into Updates vs Work Notes
  const updateComms = useMemo(
    () => currentComms.filter((c) => c.role !== 'system'),
    [currentComms]
  );
  const worknoteComms = useMemo(
    () => currentComms.filter((c) => c.role === 'system'),
    [currentComms]
  );

  const { user, logout } = useAuth();
  const userEmail = (user?.metadata as any)?.email || '';
  const incident_comms_admin = (user?.metadata as any)?.incident_comms_admin || false;
  console.log('User role determined:', { userEmail, incident_comms_admin });
  const userRole = incident_comms_admin ? 'incident_comms_admin' : 'incident_comms_user';


  const displayedComms = middleView === 'updates' ? updateComms : worknoteComms;
  const currentIncident = useMemo(
    () => incidents.find((i) => i.id === selectedId) || null,
    [incidents, selectedId]
  );
  console.log('currentIncident', currentIncident)
  const regenSummary = async () => {
    if (!currentIncident) return;
    setLoadingSummary(true);
    setTimeout(() => {
      // Chronological (oldest first) to assign stable labels
      const asc = [...currentComms].sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      // Label first as "Initial Update", subsequent as "Update N"
      const labeled = asc.map((c, idx) => ({
        comm: c,
        label: idx === 0 ? 'Initial Update' : `Update ${idx + 1}`
      }));
      // Display newest first in summary
      const desc = [...labeled].reverse();
      const lines = desc.map(({ comm, label }) =>
        `- (${label}) (${comm.role}) ${comm.author}: ${comm.content}`
      );
      setSummary(
        `Incident ${currentIncident.id} (${currentIncident.severity.toUpperCase()}): ${currentIncident.title}\n\nRecent communications (newest first):\n${lines.join('\n') || 'No updates yet.'}`
      );
      setLoadingSummary(false);
    }, 300);
  };

  // Ensure this ONLY fetches from backend (no local synthesis)
  const generateExecutiveSummary = useCallback(async () => {
    if (!currentIncident) return;
    setLoadingExecSummary(true);
    try {
      const resp = await fetchExecutiveSummary(currentIncident.id);
      if (resp.success) {
        setExecSummary(resp.executive_summary || '');
      }
    } catch (e) {
      console.error('Fetch executive summary failed', currentIncident.id, e);
    } finally {
      setLoadingExecSummary(false);
    }
  }, [currentIncident]);

  useEffect(() => {
    if (currentIncident) {
      generateExecutiveSummary();
    }
  }, [currentIncident, generateExecutiveSummary]);

  useEffect(() => {
    if (currentIncident) {
      regenSummary();
    }
  }, [currentIncident, communications]);

  useEffect(() => {
    if (currentIncident) {
      // Use the email_subject from the incident if available, otherwise use default
      const subject = currentIncident.email_subject?.trim() || 
        `Incident ${currentIncident.id} Update - ${currentIncident.title}`;
      setEmailSubject(subject);
    } else {
      setEmailSubject('');
    }
    setEmailSent(false);
  }, [currentIncident]);

  const rephraseWithLLM = useCallback(async (raw: string) => {
    setRephrasing(true);
    try {
      const resp = await generateLLMUpdates(raw);
      if (resp.success && resp.suggested_update?.trim()) {
        return resp.suggested_update.trim();
      }
      return raw.trim();
    } catch (e) {
      console.error('LLM rephrase failed', e);
      return raw.trim();
    } finally {
      setRephrasing(false);
    }
  }, []);

  const triggerRephrase = useCallback(async () => {
    if (!draftText.trim() || rephrasing) return;
    const out = await rephraseWithLLM(draftText);
    console.log('Rephrased draft:', { original: draftText, rephrased: out });
    setRephrasedDraft(out);
    setShowPreview(true);
  }, [draftText, rephraseWithLLM, rephrasing]);



  const [editingUpdateId, setEditingUpdateId] = useState<number | null>(null); // NEW
  const [editingUpdateText, setEditingUpdateText] = useState('');              // NEW

  const postIncidentUpdate = useCallback(
    async (incidentId: string, content: string) => {
      if (!incidentId) return;
      try {
        const resp = await addIncidentUpdate(
          incidentId,
          content,
          userEmail,
          false, // iscircumvented
          userRole
        );
        if (resp.success) {
          setCommunications(prev => [
            ...prev,
            {
              id: `upd-${resp.inserted.update_id}`,
              incidentId,
              author: userEmail,
              role: 'analyst',
              timestamp: resp.inserted.timestamp || new Date().toISOString(),
              content: resp.inserted.update,
              update_id: resp.inserted.update_id,
              iscircumvented: resp.inserted.circumvented || false,
              iscommunication_sent: resp.inserted.communication_sent || false,
              approved: resp.inserted.approved ?? false,              
              approval_id: resp.pending_approval?.approval_id ?? undefined  
            }
          ]);
          
          try {
            await updateIncidentState(incidentId, 'In progress');
            
            // Update local state only after successful DB update
            setIncidents(prev => prev.map(i =>
              i.id === incidentId 
                ? { ...i, state: 'In progress' } 
                : i
            ));
          } catch (stateError) {
            console.error('Failed to update incident state:', stateError);
            // Continue with the rest of the flow even if state update fails
          }
          
          regenSummary();
          await generateExecutiveSummary();
          if (showEmailForm && currentIncident) {
            setEmailHtml(
              buildIncidentEmailHTML(
                currentIncident,
                currentImpactedApps,
                communications.concat({
                  id: `upd-${resp.inserted.update_id}`,
                  incidentId,
                  author: userEmail,
                  role: 'analyst',
                  timestamp: resp.inserted.timestamp || new Date().toISOString(),
                  content: resp.inserted.update,
                  update_id: resp.inserted.update_id,
                  iscircumvented: resp.inserted.circumvented || false,
                  iscommunication_sent: resp.inserted.communication_sent || false,
                  approved: resp.inserted.approved ?? false,
                  approval_id: resp.pending_approval?.approval_id ?? undefined
                }),
                resp.inserted.approved ? execSummary : (execSummary || '')
              )
            );
          }
        }
      } catch (e) {
        console.error('Add update failed', e);
      }
    },
    [
      userEmail,
      userRole,
      regenSummary,
      generateExecutiveSummary,
      showEmailForm,
      currentIncident,
      currentImpactedApps,
      communications,
      execSummary
    ]
  );

  const acceptRephrased = () => {
    if (!currentIncident || !rephrasedDraft.trim()) return;
    const text = rephrasedDraft.trim();
    setShowPreview(false);
    setDraftText('');
    setRephrasedDraft('');
    // Optimistic (optional) – skip to rely on server response
    postIncidentUpdate(currentIncident.id, text);
  };

  const cancelDraft = () => {
    setShowPreview(false);
    setRephrasedDraft('');
    setRephrasing(false);
  };

  const handleDraftKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      triggerRephrase();
    }
  };

  useEffect(() => {
    if (showEmailForm && currentIncident) {
      const latestIncident = incidents.find(i => i.id === currentIncident.id);
      const circumventionTime = latestIncident?.circumvention_time 
        || currentIncident.circumvention_time 
        || incidentMetaDraft.circumventionTime;
      setEmailHtml(
        buildIncidentEmailHTML(
          { ...currentIncident, circumvention_time: circumventionTime },
          currentImpactedApps,
          currentComms,
          execSummary
        )
      );
    }
  }, [showEmailForm, currentIncident, currentImpactedApps, currentComms, execSummary, incidents, incidentMetaDraft.circumventionTime]);

  const selectedIdRef = useRef<string | null>(null);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  const setCurrentIncidentEmailRecipients = useCallback(
    (next: EmailRecipient[] | ((prev: EmailRecipient[]) => EmailRecipient[])) => {
      _setEmailRecipients(prev => {
        const resolved = typeof next === 'function' ? next(prev) : next;
        const sid = selectedIdRef.current;
        if (sid) {
          setEmailRecipientsMap(m => ({ ...m, [sid]: resolved }));
        }
        return resolved;
      });
    },
    []
  );

  // EXTRA sync when incident changes or email form opens
  useEffect(() => {
    if (!currentIncident) {
      _setEmailRecipients([]);
      return;
    }
    // Always sync local list from map (even if form not open yet)
    const raw = emailRecipientsMap[currentIncident.id] || [];
    _setEmailRecipients(raw);
  }, [currentIncident, emailRecipientsMap]);

  // When opening the email form ensure latest recipients loaded
  useEffect(() => {
    if (showEmailForm && currentIncident) {
      const raw = emailRecipientsMap[currentIncident.id] || [];
      _setEmailRecipients(raw);
    }
  }, [showEmailForm, currentIncident, emailRecipientsMap]);

  const clearRecipientsForIncident = useCallback((incidentId: string) => {
    setEmailRecipientsMap(m => {
      const copy = { ...m };
      delete copy[incidentId];
      return copy;
    });
    if (selectedId === incidentId) {
      _setEmailRecipients([]);
    }
  }, [selectedId]);

  const clearRecipients = () => {
    if (selectedId) clearRecipientsForIncident(selectedId);
    setEmailInput('');
  };

  useEffect(() => {
    if (currentIncident) {
      // ✅ Don't override if user has already customized the subject
      const storedSubject = currentIncident.email_subject?.trim();
      
      // Only set subject if it's empty or if incident changed
      setEmailSubject(prev => {
        // If we already have a subject and incident hasn't changed, keep it
        if (prev && prev !== `Incident ${currentIncident.id} Update - ${currentIncident.title}`) {
          return prev;
        }
        // Otherwise use stored or default
        return storedSubject || `Incident ${currentIncident.id} Update - ${currentIncident.title}`;
      });
    } else {
      setEmailSubject('');
    }
    setEmailSent(false);
  }, [currentIncident?.id]); // ✅ Only trigger on incident ID change, not full object

  const [emailAlert, setEmailAlert] = useState<{
    show: boolean;
    variant: 'info' | 'error' | 'success';
    message: string;
  } | null>(null);

  const sendEmail = async () => {
    if (!currentIncident || !emailRecipients.length || emailSending) return;
    setEmailSending(true);
    setEmailSent(false);
    try {
      const latestIncident = incidents.find(i => i.id === currentIncident.id);
      const circumventionTime = latestIncident?.circumvention_time 
        || currentIncident.circumvention_time 
        || incidentMetaDraft.circumventionTime;
      const finalSubject = emailSubject.trim() || `Incident ${currentIncident.id} Update - ${currentIncident.title}`;
      const html = currentIncident.circumvented
        ? buildIncidentEmailHTML(
            { ...currentIncident, circumvention_time: circumventionTime },
            currentImpactedApps,
            currentComms,
            execSummary
          )
        : buildIncidentEmailHTML(
            currentIncident,
            currentImpactedApps,
            currentComms,
            execSummary
          );
  
      await sendIncidentEmail({
        incident_id: currentIncident.id,
        to: emailRecipients.map(r => r.email),
        subject: finalSubject,
        html,
        text: summary
      });
      
      setEmailSent(true);

      setEmailAlert({
        show: true,
        variant: 'success',
        message: `Email sent successfully to ${emailRecipients.length} recipient${emailRecipients.length > 1 ? 's' : ''}!`
      });
  
      setTimeout(() => {
        setShowEmailForm(false);
      }, 500);

      setTimeout(() => {
        setEmailAlert(null);
      }, 4000);
      
      setIncidents(prev => prev.map(i => 
        i.id === currentIncident.id 
          ? { ...i, email_subject: finalSubject }
          : i
      ));
      
    } catch (e) {
      console.error(e);
      
      setEmailAlert({
        show: true,
        variant: 'error',
        message: 'Failed to send email. Please try again.'
      });
      
      setTimeout(() => {
        setEmailAlert(null);
      }, 5000);
    } finally {
      setEmailSending(false);
    }
  };

  const detailLoading = selectedId && detailLoadingId === selectedId && !detailLoadedRef.current.has(selectedId);

  const handleDeleteUpdate = useCallback(async (c: Communication) => {
    if (!c.update_id) return;
    if (!confirm('Delete this update?')) return;
    try {
      const resp = await deleteIncidentUpdate(c.update_id, c.incidentId);
      if (resp.success) {
        setCommunications(prev =>
          prev.filter(x => x.update_id !== c.update_id)
        );
        regenSummary();
      }
    } catch (e) {
      console.error('Delete update failed', e);
    }
  }, [regenSummary]);

  const handleApproveUpdate = useCallback(
    async (c: Communication, newContent?: string) => {
      if (!c.update_id) return;
  
      setCommunications(prev =>
        prev.map(x =>
          x.update_id === c.update_id
            ? {
                ...x,
                content: newContent !== undefined ? newContent.trim() : x.content,
                iscommunication_sent: true,
                approved: true
              }
            : x
        )
      );
  
      if (editingUpdateId === c.update_id) {
        setEditingUpdateId(null);
        setEditingUpdateText('');
      }
  
      try {
        if (newContent !== undefined && newContent.trim() !== c.content.trim()) {
          await editIncidentUpdate(c.update_id, newContent.trim());
        } else {
          await approveIncidentUpdate(c.update_id);
        }
        await generateExecutiveSummary();
        if (showEmailForm && currentIncident) {
          setEmailHtml(
            buildIncidentEmailHTML(
              currentIncident,
              currentImpactedApps,
              communications.map(x =>
                x.update_id === c.update_id
                  ? { ...x, content: newContent ? newContent.trim() : x.content, approved: true }
                  : x
              ),
              execSummary
            )
          );
        }
      } catch (e) {
        console.error('Approve/edit update failed', e);
        setCommunications(prev =>
          prev.map(x =>
            x.update_id === c.update_id
              ? { ...x, iscommunication_sent: false, approved: false, content: c.content }
              : x
          )
        );
      }
    },
    [
      editingUpdateId,
      editingUpdateText,
      generateExecutiveSummary,
      showEmailForm,
      currentIncident,
      currentImpactedApps,
      communications,
      execSummary
    ]
  );

  const startEditUpdate = (c: Communication) => {
    if (!c.update_id) return;
    setEditingUpdateId(c.update_id);
    setEditingUpdateText(c.content);
  };

  const saveEditUpdate = useCallback(() => {
    if (editingUpdateId == null) return;
    const target = communications.find(c => c.update_id === editingUpdateId);
    if (!target) return;
    const trimmed = editingUpdateText.trim();
    if (!trimmed) {
      setEditingUpdateId(null);
      setEditingUpdateText('');
      return;
    }
    handleApproveUpdate(target, trimmed);
  }, [editingUpdateId, editingUpdateText, communications, handleApproveUpdate]);

  const cancelEditUpdate = () => {
    setEditingUpdateId(null);
    setEditingUpdateText('');
  };

  const handleRejectUpdate = useCallback(
    async (c: Communication) => {
      if (!c.update_id) return;
  
      // If admin, set approval status to rejected before deleting
      if (incident_comms_admin  && c.approval_id) {
        try {
          await setApprovalStatus(c.approval_id, c.update_id, 'rejected');
        } catch (e) {
          console.error('Setting rejected status failed', e);
          // Continue with delete anyway (or abort if desired)
        }
      }
  
      // Delete update
      try {
        const resp = await deleteIncidentUpdate(c.update_id, c.incidentId);
        if (resp.success) {
          setCommunications(prev => prev.filter(x => x.update_id !== c.update_id));
          
          // ✅ NEW: If deleted update was circumvented, check if any circumvented updates remain
          if (c.iscircumvented) {
            const remainingCircumvented = resp.remaining_updates.some(u => u.circumvented);
            
            if (!remainingCircumvented) {
              // ✅ No more circumvented updates - reopen incident
              setClosedUpdates(prev => {
                const next = new Set(prev);
                next.delete(c.incidentId);
                return next;
              });
              setIncidents(prev => prev.map(i =>
                i.id === c.incidentId 
                  ? { ...i, circumvented: false, state: 'open' } 
                  : i
              ));
              
              // ✅ Also update backend to clear circumvented flag
              try {
                await reopenIncidentUpdates(c.incidentId);
              } catch (e) {
                console.error('Failed to reopen incident', e);
              }
            }
          }
          
          regenSummary();
        }
      } catch (e) {
        console.error('Reject (delete) failed', e);
      }
    },
    [regenSummary, userRole]
  );

  const MiddleDetailSkeleton = () => (
    <div className="flex flex-col gap-3 animate-pulse">
      <div className="h-6 w-40 rounded bg-muted/40" />
      <div className="h-24 rounded-xl border border-border/40 bg-background/30" />
      <div className="space-y-2">
        <div className="h-4 w-3/4 rounded bg-muted/30" />
        <div className="h-4 w-1/2 rounded bg-muted/20" />
        <div className="h-4 w-2/3 rounded bg-muted/25" />
      </div>
    </div>
  );

  const RightDetailSkeleton = () => (
    <div className="flex flex-col gap-3 animate-pulse">
      <div className="h-40 rounded-xl border border-border/40 bg-background/30" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-14 rounded-md border border-border/30 bg-muted/20" />
        ))}
      </div>
      <div className="h-5 w-44 rounded bg-muted/30 mt-2" />
      <div className="flex flex-wrap gap-1.5">
        {Array.from({ length: 4 }).map((_, i) => (
          <span
            key={i}
            className="h-5 w-20 rounded-md bg-muted/25 border border-border/30"
          />
        ))}
      </div>
    </div>
  );

  // ========== NEW: Resizable middle (Updates) / right (Summary) panels ==========
  const panelsWrapRef = useRef<HTMLDivElement | null>(null);
  const [rightWidth, setRightWidth] = useState<number>(() => {
    const stored = Number(localStorage.getItem('iu_right_w'));
    return stored >= 240 ? stored : 360; // default 360px
  });
  const draggingRef = useRef(false);
  const [edgeHover, setEdgeHover] = useState(false);

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.classList.add('select-none', 'cursor-col-resize');
  };

  const handleSummaryMouseDown = (e: React.MouseEvent) => {
    // Trigger resize only if user clicks within first 8px of left boundary
    if (e.nativeEvent.offsetX <= 8) startDrag(e);
  };

  const handleSummaryMouseMove = (e: React.MouseEvent) => {
    const near = e.nativeEvent.offsetX <= 8;
    if (near !== edgeHover) setEdgeHover(near);
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!draggingRef.current || !panelsWrapRef.current) return;
      const wrap = panelsWrapRef.current.getBoundingClientRect();
      let newWidth = wrap.right - e.clientX;
      const min = 240;
      const max = Math.min(600, wrap.width * 0.6);
      if (newWidth < min) newWidth = min;
      if (newWidth > max) newWidth = max;
      setRightWidth(newWidth);
    };
    const onUp = () => {
      if (draggingRef.current) {
        draggingRef.current = false;
        document.body.classList.remove('select-none', 'cursor-col-resize');
        localStorage.setItem('iu_right_w', String(rightWidth));
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [rightWidth]);
  // ==============================================================================

  const startRephraseClosing = async () => {
    if (!closingNotes.trim() || closingRephrasing) return;
    setClosingRephrasing(true);
    try {
      const out = await rephraseWithLLM(closingNotes);
      setClosingRephrased(out);
    } catch {
      setClosingRephrased(closingNotes.trim());
    } finally {
      setClosingRephrasing(false);
    }
  };

  const proceedToNotes = () => setClosingStep('notes');
  const proceedToPreview = () => {
    if (!closingRephrased.trim()) setClosingRephrased(closingNotes.trim());
    setClosingStep('preview');
  };
  const backFromNotes = () => setClosingStep('confirm');
  const backFromPreview = () => setClosingStep('notes');
  const cancelClosingFlow = () => {
    setClosingDialogOpen(false);
    setClosingStep('confirm');
    setClosingNotes('');
    setClosingRephrased('');
  };

  const startCloseUpdatesFlow = async () => {
    if (!currentIncident) return;
    if (isUpdatesClosed) {
      // Reopen via API
      try {
        const resp = await reopenIncidentUpdates(currentIncident.id);
        if (resp.success) {
          setClosedUpdates(prev => {
            const next = new Set(prev);
            next.delete(currentIncident.id);
            return next;
          });
          setIncidents(prev =>
            prev.map(i =>
              i.id === currentIncident.id
                ? { ...i, circumvented: false, state: 'open' }
                : i
            )
          );
          // Optionally refresh updates to reflect any backend changes
          refreshSelectedUpdates();
        }
      } catch (e) {
        console.error('Reopen updates failed', e);
      }
    } else {
          // ✅ NEW: Validate Problem Record and RCA Owner before allowing circumvention
          const missingFields: string[] = [];
      
          if (!currentIncident.problemRecord?.trim()) {
            missingFields.push('Problem Record');
          }
          if (!currentIncident.owner?.trim()) {
            missingFields.push('RCA Owner');
          }
          
          if (missingFields.length > 0) {
            alert(
              `Cannot circumvent incident. Please fill in the following required fields:\n\n• ${missingFields.join('\n• ')}\n\nUpdate these fields in the "Incident Details" section on the right panel.`
            );
            
            // ✅ Auto-open the incident details editor to help user fill missing fields
            setEditingIncidentMeta(true);
            return;
          }
          
      // Begin closure flow
      setClosingDialogOpen(true);
      setClosingStep('confirm');
      setClosingNotes('');
      setClosingRephrased('');
    }
  };

  const buildClosureEmail = useCallback(() => {
    if (!currentIncident) return '';
    const finalText = closingRephrased.trim()
      ? closingRephrased.trim()
      : closingNotes.trim();
    const closingComm = finalText
      ? [{
          id: `closing-preview-${Date.now()}`,
          incidentId: currentIncident.id,
          author: userEmail || 'analyst',
          role: 'analyst',
          timestamp: new Date().toISOString(),
          content: finalText,
          iscircumvented: true
        }]
      : [];
    const allForEmail = [...currentComms, ...closingComm];
    const augmentedExec = execSummary
      ? `${execSummary}`
      : ``;
    return buildIncidentEmailHTML(
      currentIncident,
      currentImpactedApps,
      allForEmail,
      augmentedExec,
      finalText
    );
  }, [currentIncident, currentComms, currentImpactedApps, execSummary, closingNotes, closingRephrased, userEmail]);

  const openClosureEmailPreview = useCallback(() => {
    if (!currentIncident) return;
    const html = buildClosureEmail();
    setClosureEmailHtml(html);
    setClosurePreviewOpen(true);
  }, [currentIncident, buildClosureEmail]);

  useEffect(() => {
    if (closurePreviewOpen) {
      setClosureEmailHtml(buildClosureEmail());
    }
  }, [closurePreviewOpen, buildClosureEmail]);

  const getCurrentCSTTime = (): string => {
    const now = new Date();
    
    // Convert to CST (UTC-6) or CDT (UTC-5) depending on DST
    const cstTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
    
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${cstTime.getFullYear()}-${pad(cstTime.getMonth() + 1)}-${pad(cstTime.getDate())} ${pad(cstTime.getHours())}:${pad(cstTime.getMinutes())}:${pad(cstTime.getSeconds())}`;
  };
  
  const sendClosureEmail = async () => {
    if (!currentIncident) return;
    if (!emailRecipients.length || closingEmailSending) return;
    const finalText = closingRephrased.trim()
      ? closingRephrased.trim()
      : closingNotes.trim();
    if (!finalText) return;
    setClosingEmailSending(true);
    try {
      // ✅ Determine circumvention time
      const circumventionTime = currentIncident.circumvention_time?.trim() 
        || incidentMetaDraft.circumventionTime?.trim()
        || getCurrentCSTTime();
  
      // ✅ Get latest subject
      const finalSubject = emailSubject.trim() || `Incident ${currentIncident.id} Circumvented - ${currentIncident.title}`;
  
      // Update incident metadata with circumvention time
      try {
        const updateResp = await updateIncidentMeta({
          incident_number: currentIncident.id,
          description: currentIncident.description || '',
          businessImpact: currentIncident.businessImpact || '',
          bridgeDetails: currentIncident.bridgeDetails || '',
          problemRecord: currentIncident.problemRecord || '',
          rcaOwner: currentIncident.owner || '',
          circumventionTime: circumventionTime
        });
        
        if (updateResp.success) {
          // Update local state
          setIncidents(prev => prev.map(i =>
            i.id === currentIncident.id 
              ? { 
                  ...i, 
                  circumvention_time: circumventionTime,
                  circumvented: true, 
                  state: 'circumvented' 
                } 
              : i
          ));
          
          setIncidentMetaDraft(prev => ({
            ...prev,
            circumventionTime: circumventionTime
          }));
          
        } else {
          console.warn('Failed to update circumvention time:', updateResp);
        }
      } catch (e) {
        console.error('Failed to set circumvention time', e);
      }
  
      // Persist closure update
      const insertResp = await addIncidentUpdate(
        currentIncident.id,
        finalText,
        userEmail,
        true,
        userRole
      );
      
      if (insertResp.success && insertResp.inserted) {
        setCommunications(prev => [
          ...prev,
          {
            id: `upd-${insertResp.inserted.update_id}`,
            incidentId: currentIncident.id,
            author: insertResp.inserted.updated_by || userEmail || 'analyst',
            role: 'analyst',
            timestamp: insertResp.inserted.timestamp || new Date().toISOString(),
            content: insertResp.inserted.update,
            update_id: insertResp.inserted.update_id,
            iscircumvented: true,
            iscommunication_sent: false,
            approved: insertResp.inserted.approved ?? false,
            approval_id: insertResp.pending_approval?.approval_id ?? undefined
          }
        ]);
      }
  
      // Build email with communications including the new closing note
      const allForEmail: Communication[] = [
        ...currentComms,
        {
          id: `closing-local-${Date.now()}`,
          incidentId: currentIncident.id,
          author: (userEmail || 'analyst') as string,
          role: 'analyst',
          timestamp: new Date().toISOString(),
          content: finalText,
          iscircumvented: true
        }
      ];
      
      const augmentedExec = execSummary || '';
      
      // ✅ Build HTML with updated incident object including circumvention time
      const html = buildIncidentEmailHTML(
        { ...currentIncident, circumvention_time: circumventionTime },
        currentImpactedApps,
        allForEmail,
        augmentedExec,
        finalText
      );
      
      await sendIncidentEmail({
        incident_id: currentIncident.id,
        to: emailRecipients.map(r => r.email),
        subject: finalSubject, // ✅ Use the variable instead of template string
        html,
        text: `${augmentedExec}\n\nFinal Closing Notes:\n${finalText}`
      });
      
      setClosedUpdates(prev => new Set([...prev, currentIncident.id]));
      generateExecutiveSummary();
      setClosingStep('done');

      setEmailAlert({
        show: true,
        variant: 'success', // ✅ Use success variant
        message: `Circumvention email sent successfully to ${emailRecipients.length} recipient${emailRecipients.length > 1 ? 's' : ''}!`
      });

      setTimeout(() => {
        setEmailAlert(null);
      }, 4000);
      
    } catch (e) {
      console.error('Closure email failed', e);
      setEmailAlert({
        show: true,
        variant: 'error', // ✅ Use success variant
        message: `Email failed to sent. Please try again.`
      });
    } finally {
      setClosingEmailSending(false);
    }
  };

  const dlLoadedRef = useRef<Set<string>>(new Set()); // track incidents whose DLs we auto-fetched

  useEffect(() => {
    // Close knowledge article dialog when switching incidents
    setKnowledgeArticleOpen(false);
  }, [selectedId]);

  useEffect(() => {
    // Reset meta editor on incident change
    setEditingIncidentMeta(false);
    setIncidentMetaDirty(false);
    if (currentIncident) {
      setIncidentMetaDraft({
        description: currentIncident.description || '',
        businessImpact: currentIncident.businessImpact || '',
        bridgeDetails: currentIncident.bridgeDetails || '',
        problemRecord: currentIncident.problemRecord || '',
        rcaOwner: currentIncident.owner || '',
        circumventionTime: currentIncident.circumvention_time || '' // NEW
      });
    }
  }, [currentIncident]);

  const persistIncidentMeta = async () => {
    if (!currentIncident || savingIncidentMeta || !incidentMetaDirty) {
      setEditingIncidentMeta(false);
      return;
    }
    setSavingIncidentMeta(true);
    try {
      const resp = await updateIncidentMeta({
        incident_number: currentIncident.id,
        description: incidentMetaDraft.description,
        businessImpact: incidentMetaDraft.businessImpact,
        bridgeDetails: incidentMetaDraft.bridgeDetails,
        problemRecord: incidentMetaDraft.problemRecord,
        rcaOwner: incidentMetaDraft.rcaOwner,
        circumventionTime: incidentMetaDraft.circumventionTime
      });
      if (resp.success) {
        setIncidents(prev =>
          prev.map(i =>
            i.id === currentIncident.id
              ? {
                  ...i,
                  description: resp.updated.description ?? incidentMetaDraft.description,
                  businessImpact: resp.updated.businessImpact ?? incidentMetaDraft.businessImpact,
                  bridgeDetails: resp.updated.bridgeDetails ?? incidentMetaDraft.bridgeDetails,
                  problemRecord: resp.updated.problemRecord ?? incidentMetaDraft.problemRecord,
                  owner: resp.updated.rcaOwner ?? incidentMetaDraft.rcaOwner,
                  circumvention_time: resp.updated.circumventionTime ?? incidentMetaDraft.circumventionTime // NEW
                }
              : i
          )
        );
        setIncidentMetaDirty(false);
      } else {
        console.warn('Meta update not successful');
      }
    } catch (e) {
      console.error('Failed saving incident meta', e);
    } finally {
      setSavingIncidentMeta(false);
      setEditingIncidentMeta(false);
    }
  };

  const updateCircumventionTimeBeforeSend = useCallback(async () => {
    if (!currentIncident) return;
    if (!currentIncident.circumvented) return;
    const circumventionTime = currentIncident.circumvention_time?.trim() 
      || incidentMetaDraft.circumventionTime?.trim()
      || getCurrentCSTTime();
    
    try {
      const updateResp = await updateIncidentMeta({
        incident_number: currentIncident.id,
        description: currentIncident.description || '',
        businessImpact: currentIncident.businessImpact || '',
        bridgeDetails: currentIncident.bridgeDetails || '',
        problemRecord: currentIncident.problemRecord || '',
        rcaOwner: currentIncident.owner || '',
        circumventionTime: circumventionTime
      });
      
      if (updateResp.success) {
        // Update local state immediately
        setIncidents(prev => prev.map(i =>
          i.id === currentIncident.id 
            ? { ...i, circumvention_time: circumventionTime } 
            : i
        ));
        
        setIncidentMetaDraft(prev => ({
          ...prev,
          circumventionTime: circumventionTime
        }));
        
        // ✅ Rebuild email HTML with updated time
        const html = buildIncidentEmailHTML(
          { ...currentIncident, circumvention_time: circumventionTime },
          currentImpactedApps,
          currentComms,
          execSummary
        );
        setEmailHtml(html);
        
      }
    } catch (e) {
      console.error('Failed to update circumvention time:', e);
    }
  }, [currentIncident, incidentMetaDraft.circumventionTime, currentImpactedApps, currentComms, execSummary]);

  const [targetUpdateId, setTargetUpdateId] = useState<number | null>(null);
  const [highlightUpdateId, setHighlightUpdateId] = useState<number | null>(null);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const incidentParam = sp.get('incident');
    const updateParam = sp.get('update');

    if (incidentParam) {
      setSelectedId(incidentParam);
    }
    setTargetUpdateId(updateParam ? Number(updateParam) : null);
  }, [location.search]);

  useEffect(() => {
    if (
      selectedId &&
      targetUpdateId != null &&
      updatesLoadedRef.current.has(selectedId)
    ) {
      requestAnimationFrame(() => {
        const el = document.getElementById(`update-${targetUpdateId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightUpdateId(targetUpdateId);
          setTimeout(() => setHighlightUpdateId(null), 6000);
          // Remove the update param so later incident selections don't retain it.
          navigate(`/incidentcommunication?incident=${encodeURIComponent(selectedId)}`, { replace: true });
        }
      });
    }
  }, [selectedId, targetUpdateId, communications, navigate]);

  // Build unique state options from API data
  const uniqueStates = useMemo(() => {
    const set = new Set<string>();
    incidents.forEach(i => {
      const s = (i.state || '').trim();
      if (s) set.add(s.toLowerCase());
    });
    return Array.from(set).sort(); // e.g. ["circumvented","mitigating","open","resolved"]
  }, [incidents]);

  const incidentsListRef = useRef<HTMLDivElement | null>(null);

  // Helper: scroll selected incident into the left list container (centered)
  const scrollSelectedIntoView = useCallback((id: string) => {
    const container = incidentsListRef.current;
    const el = document.getElementById(`incident-item-${id}`);
    if (!container || !el) return;

    // Use container-relative offset to avoid page-level scrolling
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const currentScroll = container.scrollTop;
    const offsetTop = elRect.top - containerRect.top + currentScroll;

    const targetTop = Math.max(
      0,
      offsetTop - Math.max(0, container.clientHeight / 2 - el.clientHeight / 2)
    );

    container.scrollTo({ top: targetTop, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    // Defer until list renders
    const to = setTimeout(() => {
      scrollSelectedIntoView(selectedId);
    }, 0);
    return () => clearTimeout(to);
  }, [selectedId, filteredIncidents.length, scrollSelectedIntoView]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const incidentParam = sp.get('incident');
    if (incidentParam) {
      const t1 = setTimeout(() => scrollSelectedIntoView(incidentParam), 50);
      const t2 = setTimeout(() => scrollSelectedIntoView(incidentParam), 250);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [location.search, filteredIncidents.length, scrollSelectedIntoView]);

  const [incidentsReady, setIncidentsReady] = useState(false);

  // Mark incidents list ready when loaded
  useEffect(() => {
    setIncidentsReady(incidents.length > 0);
  }, [incidents]);

  useEffect(() => {
    if (!incidentsReady || !selectedId) return;
    const exists = incidents.some(i => i.id === selectedId);
    if (!exists) return;
    if (updatesLoadedRef.current.has(selectedId)) return; // already loaded
    loadIncidentUpdates(selectedId);
  }, [incidentsReady, incidents, selectedId, loadIncidentUpdates]);

  const handleRecipientsChanged = useCallback((incidentId: string) => {
    dlLoadedRef.current.add(incidentId);
  }, []);

  return (<Page><>
  {/* ✅ Alert notification - positioned at top */}
  {emailAlert?.show && (
      <div className="fixed bottom-5 right-4 z-50 max-w-sm">
      <Alert 
        variant={emailAlert.variant}
        onClose={() => setEmailAlert(null)} // ✅ Add close handler
      >
        {emailAlert.message}
      </Alert>
    </div>
  )}
    <div className="flex flex-col h-full w-full p-3 gap-3">
      <div className="flex flex-1 min-h-0 gap-4">
        {/* Left: fixed width (was xl:col-span-3) */}
        <section
          className="flex flex-col bg-accent dark:bg-card border border-border/50 rounded-2xl p-3 min-h-0 shrink-0"
          style={{ width: 300 }}
        >
          {/* Header */}
          <div className="mb-2">
            <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
              Incidents
            </h2>
            <input
              placeholder="Filter…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="flex-1 text-xs rounded-md px-2 py-1 bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50"
            />
          </div>
          {/* Filter + Refresh (own row to avoid horizontal overflow) */}
          <div className="mb-3">
            <div className="grid grid-cols-2 gap-2">
              {/* NEW: State filter */}
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className="text-[10px] px-2 py-1 rounded-md bg-background/60 dark:bg-background/40 border border-border/50 w-full"
                title="Filter by state"
              >
                <option value="">All states</option>
                {uniqueStates.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>

              {/* NEW: Circumvented filter */}
              <select
                value={circumventedFilter}
                onChange={(e) => setCircumventedFilter(e.target.value as any)}
                className="text-[10px] px-2 py-1 rounded-md bg-background/60 dark:bg-background/40 border border-border/50 w-full"
                title="Filter by circumvented"
              >
                <option value="all">All</option>
                <option value="yes">Circumvented</option>
                <option value="no">Not circumvented</option>
              </select>
            </div>

            <div className="mt-2">
              <button
                onClick={refreshIncidents}
                disabled={loadingIncidents}
                title="Refresh incidents"
                className="w-full text-[10px] px-2 py-1 rounded-md border border-border/50 hover:bg-[#18E78F]/15 disabled:opacity-40 transition-colors"
              >
                <span className="inline-flex items-center justify-center gap-1">
                  <RotateCcw className={`w-3.5 h-3.5 ${loadingIncidents ? 'animate-spin' : ''}`} />
                  <span>{loadingIncidents ? 'Refreshing…' : 'Refresh'}</span>
                </span>
              </button>
            </div>
          </div>
          <div
            className="overflow-y-auto custom-scrollbar incidents-scroll space-y-2 pr-1"
            ref={incidentsListRef}
          >
            {filteredIncidents.map((i) => {
              const active = i.id === selectedId;
              const loadingDetail = detailLoadingId === i.id;
              return (
                <button
                  key={i.id}
                  id={`incident-item-${i.id}`} // add stable id for scrolling
                  onClick={() => {
                    setSelectedId(i.id);
                    navigate(`/incidentcommunication?incident=${encodeURIComponent(i.id)}`);
                    scrollSelectedIntoView(i.id); // <<< use helper
                  }}
                  className={clsx(
                    'w-full text-left group rounded-xl px-3 py-2 transition-colors border',
                    active
                      ? 'bg-[#18E78F]/15 border-[#18E78F]/50'
                      : 'bg-background/50 dark:bg-background/30 border-border/40 hover:bg-[#18E78F]/10'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={clsx(
                        'text-xs font-semibold tracking-wide flex items-center gap-1',
                        active && 'text-primary'
                      )}
                    >
                      {i.id}
                      {loadingDetail && (
                        <span className="w-2 h-2 rounded-full bg-[#18E78F] animate-pulse" />
                      )}
                    </span>
                    <span
                      className={clsx(
                        'text-[10px] px-1.5 py-0.5 rounded-full font-medium',
                        i.severity === 'P1' && 'bg-red-600/20 text-red-400',
                        i.severity === 'P2' && 'bg-orange-500/20 text-orange-400',
                        i.severity === 'P3' && 'bg-yellow-500/20 text-yellow-400',
                        i.severity === 'P4' && 'bg-emerald-500/20 text-emerald-400'
                      )}
                    >
                      {i.severity}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-start gap-2">
                    <p className="text-xs line-clamp-2 text-muted-foreground flex-1">
                      {i.title || i.id}
                    </p>
                    {i.configuration_class && (
                      <span
                        className={clsx(
                          'text-[9px] px-1.5 py-0.5 rounded-md border uppercase tracking-wide',
                          active
                            ? 'border-[#18E78F]/50 bg-[#18E78F]/15 text-[#18E78F]'
                            : 'border-border/40 bg-background/40 text-muted-foreground/70'
                        )}
                        title={i.configuration_class}
                      >
                        {i.configuration_class}
                      </span>
                    )}
                    {i.ka_url && (
                      <span
                        className="text-[9px] px-1 py-0.5 rounded-md bg-[#18E78F]/25 text-[#18E78F] font-semibold"
                        title="Knowledge Article generated"
                      >
                        KA
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-1 text-[10px] text-muted-foreground/70">
                    <span>{i.assigned_to || 'unassigned'}</span>
                    <time
                      dateTime={i.updatedAt}
                      title={`Updated ${timeAgo(i.updatedAt)}`}
                      className="tabular-nums"
                    >
                      {formatDateTime(i.updatedAt)}
                    </time>
                  </div>
                </button>
              );
            })}
            {filteredIncidents.length === 0 && (
              <div className="text-xs text-muted-foreground py-4 text-center">
                No incidents found.
              </div>
            )}
          </div>
        </section>

        {/* Wrapper for middle + right with draggable handle */}
        <div
          ref={panelsWrapRef}
          className="flex flex-1 min-w-0 gap-4 relative "
        >
          {/* Middle: Updates / Work Notes (flex-grow) */}
          <section
            className="flex flex-col bg-accent dark:bg-card border border-border/50 rounded-2xl p-3 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar incidents-scroll space-y-2 pr-0.5"
            style={{ width: `calc(100% - ${rightWidth}px - 16px)` }} // full gap subtraction
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
                {currentIncident
                  ? `${middleView === 'updates' ? 'Updates' : 'Work Notes'} – ${currentIncident.id}`
                  : 'Select an Incident'}
              </h2>
              <div className="flex items-center gap-2">
                {middleView === 'updates' && currentIncident && (
                  <button
                    onClick={refreshSelectedUpdates}
                    disabled={!currentIncident || updatesRefreshing || updatesLoadingId === currentIncident.id}
                    className="text-[10px] px-2 py-1 rounded-md border border-border/50 hover:bg-[#18E78F]/15 disabled:opacity-40 transition-colors"
                    title="Refresh incident updates"
                  >
                    {updatesRefreshing || updatesLoadingId === currentIncident.id ? '…' : 'Reload Updates'}
                  </button>
                )}
                {middleView === 'updates' && currentIncident && (
                  <button
                    onClick={startCloseUpdatesFlow}
                    className="text-[10px] px-2 py-1 rounded-md border border-border/50 hover:bg-[#18E78F]/15 transition-colors"
                    title={isUpdatesClosed ? 'Reopen updates' : 'Close updates & send final'}
                  >
                    {isUpdatesClosed ? 'Reopen Updates' : 'Circumvent Updates'}
                  </button>
                )}
                <div className="inline-flex rounded-md overflow-hidden border border-border/50">
                  <button
                    type="button"
                    onClick={() => setMiddleView('updates')}
                    className={`text-[10px] px-3 py-1 font-medium transition-colors ${
                      middleView === 'updates'
                        ? 'bg-[#18E78F]/90 text-black'
                        : 'bg-background/40 hover:bg-background/60'
                    }`}
                  >
                    Updates ({updateComms.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setMiddleView('worknotes')}
                    className={`text-[10px] px-3 py-1 font-medium transition-colors ${
                      middleView === 'worknotes'
                        ? 'bg-[#18E78F]/90 text-black'
                        : 'bg-background/40 hover:bg-background/60'
                    }`}
                  >
                    Work Notes ({worknoteComms.length})
                  </button>
                </div>
              </div>
            </div>

            {currentIncident && detailLoading ? (
              <MiddleDetailSkeleton />
            ) : currentIncident &&
              middleView === 'updates' &&
              !hasPersistedApps ? (
              // Onboarding (unchanged content below)
              <div className="flex flex-col gap-4 flex-1 overflow-y-auto custom-scrollbar pr-1 pb-2">
                <div className="rounded-xl border border-border/50 bg-background/40 dark:bg-background/30 p-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center justify-between">
                    <span>Impacted Applications</span>
                    <button
                      type="button"
                      onClick={loadApps}
                      disabled={loadingApps}
                      className="text-[9px] px-2 py-0.5 rounded-md border border-border/40 hover:bg-[#18E78F]/10 disabled:opacity-40"
                      title="Reload application list"
                    >
                      {loadingApps ? '…' : 'Reload'}
                    </button>
                  </h3>

                  {/* Selected tags */}
                  {currentImpactedApps.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {currentImpactedApps.map((app) => (
                        <span
                          key={app}
                          className="group inline-flex items-center gap-1 rounded-md bg-[#18E78F]/15 border border-[#18E78F]/40 px-2 py-0.5 text-[11px] font-medium text-[#18E78F]"
                        >
                          {app}
                          <button
                            aria-label="Remove"
                            onClick={() => removeImpactedApp(app)}
                            className="p-0.5 rounded-sm hover:bg-[#18E78F]/20"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Search / add input */}
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
                    <input
                      ref={appInputRef}
                      value={appSearch}
                      onChange={(e) => setAppSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && filteredApps[0]) {
                          e.preventDefault();
                          addImpactedApp(filteredApps[0].name);
                        }
                      }}
                      placeholder="Search & press Enter to add…"
                      className="w-full pl-7 pr-2 py-2 rounded-lg text-xs bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50"
                    />
                  </div>

                  {/* Suggestions */}
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {filteredApps.map((app) => (
                      <button
                        key={app.number}
                        onClick={() => addImpactedApp(app.name)}
                        className="text-[10px] truncate px-2 py-1 rounded-md border border-border/40 hover:border-[#18E78F]/50 hover:bg-[#18E78F]/10 transition-colors text-muted-foreground hover:text-[#18E78F]"
                      >
                        {app.name}
                      </button>
                    ))}
                    {filteredApps.length === 0 && (
                      <div className="col-span-full text-[10px] text-muted-foreground/60">
                        No matches
                      </div>
                    )}
                  </div>

                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      disabled={
                        !selectedId ||
                        !currentImpactedApps.length ||
                        savingImpacted ||
                        !dirtyForSelected
                      }
                      onClick={() => selectedId && persistImpactedApps(selectedId)}
                      className={clsx(
                        'text-[10px] px-3 py-1 rounded-md border transition-colors font-medium',
                        dirtyForSelected
                          ? 'border-[#18E78F]/50 bg-[#18E78F]/10 hover:bg-[#18E78F]/20 text-[#18E78F]'
                          : 'border-border/40 bg-background/40 text-muted-foreground cursor-default'
                      )}
                    >
                      {savingImpacted
                        ? 'Saving…'
                        : dirtyForSelected
                          ? 'Save Impacted Apps'
                          : 'Saved'}
                    </button>
                  </div>

                  <p className="mt-3 text-[10px] text-muted-foreground/60">
                    Add and save impacted applications first. Once saved, this setup panel will be replaced by the Updates stream.
                  </p>
                </div>

                {/* Once apps selected, show draft composer even before first update */}
                {currentImpactedApps.length > 0 && (
                  <div className="mt-auto">
                    {/* Rephrased preview (inline editable) */}
                    {showPreview && (
                      <div className="mb-2 border border-[#18E78F]/40 bg-[#18E78F]/10 rounded-xl p-3 text-xs">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-[11px] tracking-wide uppercase text-[#18E78F]">
                            Suggested Rephrasing
                          </span>
                          <div className="flex gap-1">
                            <button
                              onClick={acceptRephrased}
                              className="text-[10px] px-2 py-1 rounded-md bg-[#18E78F]/90 text-black font-semibold hover:bg-[#18E78F] transition-colors"
                            >
                              Post
                            </button>
                            <button
                              onClick={cancelDraft}
                              className="text-[10px] px-2 py-1 rounded-md border border-transparent hover:border-border/50 hover:bg-background/50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                        <textarea
                          value={rephrasedDraft}
                          onChange={(e) => setRephrasedDraft(e.target.value)}
                          rows={3}
                          className="w-full resize-none rounded-lg bg-background/70 dark:bg-background/40 border border-border/40 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50 px-2 py-2 text-[11px] leading-relaxed"
                          placeholder="Edit the rephrased update before posting…"
                        />
                      </div>
                    )}
                    <div className="relative">
                      <textarea
                        ref={inputRef}
                        rows={2}
                        placeholder="Type first update and press Enter (Shift+Enter = newline)…"
                        disabled={rephrasing}
                        value={draftText}
                        onChange={(e) => setDraftText(e.target.value)}
                        onKeyDown={handleDraftKeyDown}
                        className="w-full resize-none rounded-xl bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/60 px-3 py-1.5 text-xs pr-20 placeholder:text-muted-foreground/50"
                      />
                      <div className="absolute right-2 bottom-2 flex items-center gap-2">
                        {draftText && !showPreview && (
                          <button
                            onClick={triggerRephrase}
                            disabled={rephrasing}
                            className="text-[10px] px-2 py-1 rounded-md border border-[#18E78F]/40 hover:bg-[#18E78F]/15 disabled:opacity-40 transition-colors"
                          >
                            {rephrasing ? '…' : 'Rephrase'}
                          </button>
                        )}
                        {showPreview && (
                          <button
                            onClick={acceptRephrased}
                            className="text-[10px] px-2 py-1 rounded-md bg-[#18E78F]/90 text-black font-semibold hover:bg-[#18E78F]/60 transition-colors"
                          >
                            Post
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="mt-1 text-[10px] text-muted-foreground/60">
                      Enter = rephrase & preview · Shift+Enter = newline
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Display selected communication set */}
                <div className="flex-1 flex flex-col gap-2 overflow-y-auto overflow-x-hidden custom-scrollbar pr-1 pb-2">
                  {displayedComms
                    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                    .map((c) => (
                      <div
                        key={c.id}
                        id={c.update_id ? `update-${c.update_id}` : undefined}
                        className={clsx(
                          'w-full rounded-xl px-3 py-2 border text-[11px] whitespace-pre-wrap break-words group relative transition-colors',
                          c.iscircumvented && c.approved
                            ? 'bg-[#18E78F]/25 border-[#18E78F] ring-1 ring-inset ring-[#18E78F]/40'
                            : c.role === 'analyst' && 'bg-background/60 dark:bg-background/40 border-border/40',
                          !c.iscircumvented && c.role === 'automation' && 'bg-[#18E78F]/10 border-[#18E78F]/30',
                          !c.iscircumvented && c.role === 'system' && 'bg-muted/40 dark:bg-muted/20 border-border/30',
                          highlightUpdateId === c.update_id && 'ring-2 ring-inset ring-[#18E78F]'
                        )}
                      >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                          {c.author}
                          {c.iscircumvented && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-[#18E78F] text-black font-semibold">
                              Circumvented
                            </span>
                          )}
                          {c.update_id && (() => {
                            // ✅ Debug: log the exact values being checked
                            const shouldShowBadge = !c.approved;
                            
                            return (
                              <>
                                {incident_comms_admin ? (
                                  // Admin: show action buttons only if not approved/sent
                                  !c.approved && !c.iscommunication_sent ? (
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => handleApproveUpdate(c)}
                                        className="p-1 rounded-md border border-green-500/40 bg-green-500/15 hover:bg-green-500/25 text-green-700"
                                        title="Accept update"
                                        aria-label="Accept update"
                                      >
                                        <Check className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        onClick={() => startEditUpdate(c)}
                                        className="p-1 rounded-md border border-blue-500/40 bg-blue-500/15 hover:bg-blue-500/25 text-blue-700"
                                        title="Edit update"
                                        aria-label="Edit update"
                                      >
                                        <Pencil className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        onClick={() => handleRejectUpdate(c)}
                                        className="p-1 rounded-md border border-red-500/40 bg-red-500/15 hover:bg-red-500/25 text-red-600"
                                        title="Reject (delete) update"
                                        aria-label="Reject update"
                                      >
                                        <XCircle className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  ) : null
                                ) : (
                                  // Non-admin: show "Pending Approval" badge if not approved
                                  <>
                                    {shouldShowBadge && (
                                      <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-yellow-500/25 border border-yellow-500/40 text-yellow-700 font-semibold">
                                        Pending Approval
                                      </span>
                                    )}
                                  </>
                                )}
                              </>
                            );
                          })()}
                        </span>
                        <span className="text-[10px] text-muted-foreground/70">
                          {formatDateTime(c.timestamp)}
                        </span>
                      </div>
                      {editingUpdateId === c.update_id ? (
                        <div className="mt-1 space-y-2">
                          <textarea
                            value={editingUpdateText}
                            onChange={(e) => setEditingUpdateText(e.target.value)}
                            rows={3}
                            className="w-full resize-none rounded-md bg-background/70 dark:bg-background/40 border border-border/40 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50 px-2 py-1.5 text-[11px]"
                          />
                          <div className="flex gap-2">
                          <button
                            onClick={() => {
                              saveEditUpdate();
                            }}
                            className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-md bg-[#18E78F]/90 text-black font-semibold hover:bg-[#18E78F]"
                          >
                            <Check className="w-3 h-3" />
                            Save & Accept
                          </button>
                            <button
                              onClick={cancelEditUpdate}
                              className="text-[10px] px-2 py-1 rounded-md border border-border/40 hover:bg-background/60"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <MarkdownIncidentComm allowHtml={false} latex={false}>
                          {c.content}
                        </MarkdownIncidentComm>
                      )}

                      {c.update_id && c.role !== 'system' && (
                        <button
                          onClick={() => handleDeleteUpdate(c)}
                          title="Delete update"
                          aria-label="Delete update"
                          className={clsx(
                            'opacity-0 group-hover:opacity-100 transition-opacity absolute top-1 right-1 p-1 rounded-md border',
                            c.iscircumvented
                              ? 'border-[#18E78F]/40 hover:bg-[#18E78F]/30 hover:text-black'
                              : 'border-border/40 hover:bg-red-500/10 hover:text-red-400'
                          )}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                  {displayedComms.length === 0 && currentIncident && (
                    <div className="text-xs text-muted-foreground/70 py-4 text-center">
                      {middleView === 'worknotes'
                        ? 'No work notes yet.'
                        : 'No updates yet. Post the first update using the composer below.'}
                    </div>
                  )}
                  {!currentIncident && (
                    <div className="text-xs text-muted-foreground/70 py-6 text-center">
                      Select an incident to view activity.
                    </div>
                  )}
                </div>
                {/* Rephrasing preview & composer ONLY for Updates tab */}
                {middleView === 'updates' && isUpdatesClosed && (
                  <div className="mt-2 text-[10px] text-muted-foreground/70 border border-border/40 rounded-md p-3 bg-background/40">
                    Updates closed. Reopen to post more.
                  </div>
                )}
                {middleView === 'updates' && !isUpdatesClosed && (
                  <>
                    {showPreview && (
                      <div className="mt-3 mb-2 border border-[#18E78F]/40 bg-[#18E78F]/10 rounded-xl p-3 text-xs animate-in fade-in">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-[11px] tracking-wide uppercase text-[#18E78F]">
                            Suggested Rephrasing
                          </span>
                          <div className="flex gap-1">
                            <button
                              onClick={acceptRephrased}
                              className="text-[10px] px-2 py-1 rounded-md bg-[#18E78F]/90 text-black font-semibold hover:bg-[#18E78F] transition-colors"
                            >
                              Post
                            </button>
                            <button
                              onClick={cancelDraft}
                              className="text-[10px] px-2 py-1 rounded-md border border-transparent hover:border-border/50 hover:bg-background/50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                        <textarea
                          value={rephrasedDraft}
                          onChange={(e) => setRephrasedDraft(e.target.value)}
                          rows={3}
                          className="w-full resize-none rounded-lg bg-background/70 dark:bg-background/40 border border-border/40 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50 px-2 py-2 text-[11px] leading-relaxed"
                          placeholder="Edit the rephrased update before posting…"
                        />
                        <p className="mt-1 text-[10px] text-muted-foreground/60">
                          Edit freely, then Post or Cancel.
                        </p>
                      </div>
                    )}
                    <div className="mt-2">
                      <label className="sr-only">Add update</label>
                      <div className="relative">
                        <textarea
                          ref={inputRef}
                          rows={2}
                          placeholder={
                            currentIncident
                              ? 'Type your update and press Enter (Shift+Enter for newline)…'
                              : 'Select an incident first…'
                          }
                          disabled={!currentIncident || rephrasing}
                          value={draftText}
                          onChange={(e) => setDraftText(e.target.value)}
                          onKeyDown={handleDraftKeyDown}
                          className="w-full resize-none rounded-xl bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/60 px-3 py-1.5 text-xs pr-20 placeholder:text-muted-foreground/50"
                        />
                        <div className="absolute right-2 bottom-2 flex items-center gap-2">
                          {draftText && !showPreview && (
                            <button
                              onClick={triggerRephrase}
                              disabled={rephrasing}
                              className="text-[10px] px-2 py-1 rounded-md border border-[#18E78F]/40 hover:bg-[#18E78F]/15 disabled:opacity-40 transition-colors"
                            >
                              {rephrasing ? '…' : 'Rephrase'}
                            </button>
                          )}
                          {showPreview && (
                            <button
                              onClick={acceptRephrased}
                              className="text-[10px] px-2 py-1 rounded-md bg-[#18E78F]/90 text-black font-semibold hover:bg-[#18E78F]/60 transition-colors"
                            >
                              Post
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="mt-1 text-[10px] text-muted-foreground/60">
                        Enter = rephrase & preview · Shift+Enter = newline
                      </p>
                    </div>
                  </>
                )}
              </>
            )}
          </section>

          {/* SUMMARY PANEL - RESIZABLE VIA LEFT BORDER */}
          <section
            className={clsx(
              'flex flex-col h-full bg-accent dark:bg-card border border-border/50 rounded-2xl p-3 min-h-0 shrink-0 relative',
              edgeHover && !draggingRef.current && 'cursor-col-resize',
              draggingRef.current && 'cursor-col-resize'
            )}
            style={{
              width: rightWidth,
              userSelect: draggingRef.current ? 'none' : 'auto'
            }}
            onMouseDown={handleSummaryMouseDown}
            onMouseMove={handleSummaryMouseMove}
          >
            {/* Resize edge */}
            <div
              className="absolute top-0 left-0 h-full"
              style={{
                width: 6,
                cursor: 'col-resize',
                background: edgeHover ? 'rgba(24,231,143,0.25)' : 'transparent',
                transition: 'background 120ms'
              }}
            />
            <div className="flex items-center justify-between mb-3 gap-2 shrink-0">
              <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
                Summary
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={regenSummary}
                  disabled={!currentIncident || loadingSummary}
                  className="text-[10px] px-2 py-1 rounded-md border border-border/50 hover:bg-[#18E78F]/15 disabled:opacity-40 transition-colors"
                >
                  {loadingSummary ? '…' : 'Refresh'}
                </button>
                <button
                  onClick={() => {
                    if (!currentIncident) return;
                    setShowEmailForm(v => !v);
                    setEmailSent(false);
                  }}
                  disabled={!currentIncident}
                  className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-border/50 hover:bg-[#18E78F]/15 disabled:opacity-40 transition-colors"
                  title="Send as email"
                >
                  <Mail className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Email</span>
                </button>
                {/* <button
                  onClick={() => setKnowledgeArticleOpen(true)}
                  disabled={
                    !currentIncident ||
                    !(currentIncident.state === 'circumvented' || isUpdatesClosed)
                  }
                  className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-border/50 hover:bg-[#18E78F]/15 disabled:opacity-40 transition-colors"
                  title="Create Knowledge Article"
                >
                  <span className="w-3.5 h-3.5 inline-block rounded-sm bg-[#18E78F]/30" />
                  <span className="hidden sm:inline">Create KA</span>
                </button> */}
              </div>
            </div>

            <div className="flex flex-col flex-1 min-h-0">
              {detailLoading && currentIncident ? (
                <RightDetailSkeleton />
              ) : (
                <>
                  {currentIncident && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[10px] shrink-0">
                      <Stat label="Severity" value={currentIncident.severity} />
                      <Stat label="State" value={currentIncident.state} />
                      <Stat label="Config Class" value={currentIncident.configuration_class || '-'} />
                      <Stat label="Updates" value={currentComms.length.toString()} />
                      <Stat
                      label="Analysts"
                      value={
                        (
                        [
                          ...new Set(
                          currentComms
                            .filter((c) => c.role === 'analyst')
                            .map((c) => c.author)
                          )
                        ].length || 0
                        ).toString()
                      }
                      />
                      <Stat
                      label="Impacted Apps"
                      value={currentImpactedApps.length.toString()}
                      />
                    </div>
                  )}

                  {/* Impacted Apps */}
                  {currentIncident && (
                    <div className="mt-4 shrink-0">
                      {currentIncident && (
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
                              Impacted Applications
                            </h4>
                            <div className="flex items-center gap-1">
                              {editingRightApps ? (
                                <>
                                  <button
                                    onClick={() =>
                                      persistImpactedApps(currentIncident.id)
                                    }
                                    disabled={
                                      savingImpacted ||
                                      !impactedDirty[currentIncident.id]
                                    }
                                    className={clsx(
                                      'text-[9px] px-2 py-0.5 rounded-md border',
                                      impactedDirty[currentIncident.id]
                                        ? 'border-[#18E78F]/50 bg-[#18E78F]/10 hover:bg-[#18E78F]/20'
                                        : 'border-border/40 bg-background/40 text-muted-foreground cursor-default'
                                    )}
                                  >
                                    {savingImpacted
                                      ? 'Saving…'
                                      : impactedDirty[currentIncident.id]
                                        ? 'Save'
                                        : 'Saved'}
                                  </button>
                                  <button
                                    onClick={() => {
                                      // Revert to last saved
                                      const snap =
                                        lastSavedAppsRef.current[currentIncident.id] || [];
                                      setImpactedApps((prev) => ({
                                        ...prev,
                                        [currentIncident.id]: [...snap]
                                      }));
                                      setImpactedDirty(d => ({
                                        ...d,
                                        [currentIncident.id]: false
                                      }));
                                      setEditingRightApps(false);
                                    }}
                                    className="text-[9px] px-2 py-0.5 rounded-md border border-border/40 hover:bg-background/60"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => setEditingRightApps(true)}
                                  className="text-[9px] px-2 py-0.5 rounded-md border border-border/40 hover:bg-background/60"
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                          </div>

                          {!editingRightApps && currentImpactedApps.length === 0 && (
                            <div className="text-[10px] text-muted-foreground/60">
                              None added.
                            </div>
                          )}

                          {!editingRightApps && currentImpactedApps.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {currentImpactedApps.map((app) => (
                                <span
                                  key={app}
                                  className="text-[10px] px-2 py-0.5 rounded-md bg-[#18E78F]/15 border border-[#18E78F]/40 text-[#18E78F] font-medium"
                                >
                                  {app}
                                </span>
                              ))}
                            </div>
                          )}

                          {editingRightApps && (
                            <div className="flex flex-col gap-2">
                              <div className="flex flex-wrap gap-1.5">
                                {currentImpactedApps.map((app) => (
                                  <span
                                    key={app}
                                    className="group inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md bg-[#18E78F]/15 border border-[#18E78F]/40 text-[#18E78F]"
                                  >
                                    {app}
                                    <button
                                      onClick={() => removeImpactedApp(app)}
                                      className="p-0.5 rounded-sm hover:bg-[#18E78F]/25"
                                      aria-label="Remove app"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <input
                                  placeholder="Add application…"
                                  value={appSearch}
                                  onChange={(e) => setAppSearch(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && filteredApps[0]) {
                                      e.preventDefault();
                                      addImpactedApp(filteredApps[0].name);
                                    }
                                  }}
                                  className="flex-1 text-[10px] px-2 py-1 rounded-md bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50"
                                />
                                <button
                                  type="button"
                                  disabled={!filteredApps[0]}
                                  onClick={() => filteredApps[0] && addImpactedApp(filteredApps[0].name)}
                                  className="text-[10px] px-2 py-1 rounded-md border border-border/50 hover:bg-[#18E78F]/15 disabled:opacity-40"
                                >
                                  Add
                                </button>
                              </div>
                              {filteredApps.length > 0 && appSearch && (
                                <div className="flex flex-wrap gap-1">
                                  {filteredApps.slice(0, 6).map((a) => (
                                    <button
                                      key={a.number}
                                      onClick={() => addImpactedApp(a.name)}
                                      className="text-[9px] px-2 py-0.5 rounded-md border border-border/40 hover:border-[#18E78F]/50 hover:bg-[#18E78F]/10"
                                    >
                                      {a.name}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Incident Details (NEW) */}
                  {currentIncident && !showEmailForm && (
                    <div className="mt-6">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
                          Incident Details
                        </h4>
                        {!editingIncidentMeta && (
                          <button
                            onClick={() => setEditingIncidentMeta(true)}
                            className="text-[9px] px-2 py-0.5 rounded-md border border-border/40 hover:bg-background/60"
                          >
                            Edit
                          </button>
                        )}
                      </div>

                      {/* Read-only view */}
                      {!editingIncidentMeta && (
                        <div className="space-y-2 text-[10px]">
                          <DetailRow
                            label="Incident Description"
                            value={currentIncident.description || '—'}
                          />
                          <DetailRow
                            label="Business Impact"
                            value={currentIncident.businessImpact || '—'}
                          />
                          <DetailRow
                            label="Bridge Details"
                            value={currentIncident.bridgeDetails || '—'}
                          />
                          <DetailRow
                            label="Problem Record"
                            value={currentIncident.problemRecord || '—'}
                          />
                          <DetailRow
                            label="RCA Owner"
                            value={currentIncident.owner || '—'}
                          />
                          {/* ✅ Only show circumvention time if incident is circumvented */}
                          <DetailRow
                            label="Circumvention Time (CST)"
                            value={currentIncident.circumvention_time || '—'}
                          />
                        </div>
                      )}

                      {/* Editing view */}
                      {editingIncidentMeta && (
                        <div className="space-y-2">
                          <EditableArea
                            label="Incident Description"
                            value={incidentMetaDraft.description}
                            onChange={(v) => {
                              setIncidentMetaDraft(d => ({ ...d, description: v }));
                              setIncidentMetaDirty(true);
                            }}
                          />
                          <EditableArea
                            label="Business Impact"
                            value={incidentMetaDraft.businessImpact}
                            onChange={(v) => {
                              setIncidentMetaDraft(d => ({ ...d, businessImpact: v }));
                              setIncidentMetaDirty(true);
                            }}
                          />
                          <EditableArea
                            label="Bridge Details"
                            value={incidentMetaDraft.bridgeDetails}
                            onChange={(v) => {
                              setIncidentMetaDraft(d => ({ ...d, bridgeDetails: v }));
                              setIncidentMetaDirty(true);
                            }}
                          />
                          <EditableArea
                            label="Problem Record"
                            value={incidentMetaDraft.problemRecord}
                            onChange={(v) => {
                              setIncidentMetaDraft(d => ({ ...d, problemRecord: v }));
                              setIncidentMetaDirty(true);
                            }}
                          />
                          <EditableArea
                            label="RCA Owner"
                            singleLine
                            value={incidentMetaDraft.rcaOwner}
                            onChange={(v) => {
                              setIncidentMetaDraft(d => ({ ...d, rcaOwner: v }));
                              setIncidentMetaDirty(true);
                            }}
                          />
                          <EditableArea
                            label="Circumvention Time (CST)"
                            isDateTime={true}
                            value={incidentMetaDraft.circumventionTime}
                            onChange={(v) => {
                              setIncidentMetaDraft(d => ({ ...d, circumventionTime: v }));
                              setIncidentMetaDirty(true);
                            }}
                            placeholder={
                              currentIncident.circumvented
                                ? "Select date"
                                : "Available after incident is circumvented"
                            }
                            disabled={!currentIncident.circumvented} // ✅ NEW: disable if not circumvented
                          />
                          <div className="flex justify-end gap-2 pt-1">
                            <button
                              onClick={() => {
                                setEditingIncidentMeta(false);
                                setIncidentMetaDirty(false);
                                setIncidentMetaDraft({
                                  description: currentIncident.description || '',
                                  businessImpact: currentIncident.businessImpact || '',
                                  bridgeDetails: currentIncident.bridgeDetails || '',
                                  problemRecord: currentIncident.problemRecord || '',
                                  rcaOwner: currentIncident.owner || '',
                                  circumventionTime: currentIncident.circumvention_time || ''
                                });
                              }}
                              className="text-[9px] px-2 py-1 rounded-md border border-border/40 hover:bg-background/50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={persistIncidentMeta}
                              disabled={!incidentMetaDirty || savingIncidentMeta}
                              className="text-[9px] px-3 py-1 rounded-md border border-[#18E78F]/50 bg-[#18E78F]/10 hover:bg-[#18E78F]/20 disabled:opacity-40 font-medium"
                            >
                              {savingIncidentMeta
                                ? 'Saving…'
                                : incidentMetaDirty
                                  ? 'Save'
                                  : 'Saved'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Email form fills remaining height */}
                  {showEmailForm && currentIncident && (
                    <EmailSummary
                      key={currentIncident.id}        // <<< ensures component resets per incident
                      currentIncident={currentIncident}
                      currentImpactedApps={currentImpactedApps}
                      currentComms={currentComms}
                      execSummary={execSummary}
                      setExecSummary={setExecSummary}
                      generateExecutiveSummary={generateExecutiveSummary}
                      loadingExecSummary={loadingExecSummary}
                      emailRecipients={emailRecipients}
                      setEmailRecipients={setCurrentIncidentEmailRecipients}
                      emailInput={emailInput}
                      setEmailInput={setEmailInput}
                      emailSubject={emailSubject}
                      setEmailSubject={setEmailSubject}
                      emailSending={emailSending}
                      emailSent={emailSent}
                      setEmailSent={setEmailSent}
                      sendEmail={sendEmail}
                      emailHtml={emailHtml}
                      setEmailHtml={setEmailHtml}
                      setPreviewOpen={setPreviewOpen}
                      close={() => setShowEmailForm(false)}
                      onBeforeSend={updateCircumventionTimeBeforeSend} // ✅ NEW: pass the handler
                    />
                  )}
                </>
              )}
            </div>
            {/* Dialog remains outside flex-1 for overlay */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
              <DialogContent className="max-w-4xl w-[92vw] h-[80vh] p-4 flex flex-col">
                <DialogHeader className="pb-2">
                  <DialogTitle className="text-sm">
                    Email Preview {currentIncident ? `– ${currentIncident.id}` : ''}
                  </DialogTitle>
                </DialogHeader>
                <div className="flex-1 min-h-0 rounded-md border border-border/50 bg-background/60 relative flex">
                  {emailHtml ? (
                    <EmailIframePreview html={emailHtml} full />
                  ) : (
                    <div className="m-auto text-xs text-muted-foreground">
                      Generating preview…
                    </div>
                  )}
                </div>
                <div className="pt-3 flex justify-end gap-2">
                  {/* <button
                    onClick={() => {
                      const h = buildIncidentEmailHTML(
                        currentIncident ? currentIncident : DUMMY_INCIDENT,
                        currentImpactedApps,
                        currentComms,
                        execSummary
                      );
                      setEmailHtml(h);
                      navigator.clipboard.writeText(h).catch(() => {});
                    }}
                    className="text-[10px] px-3 py-1 rounded-md border border-[#18E78F]/40 bg-[#18E78F]/10 hover:bg-[#18E78F]/20 font-medium"
                  >
                    Copy HTML
                  </button> */}
                  <button
                    onClick={sendEmail}
                    disabled={
                      emailSending ||
                      !emailRecipients.length ||
                      !emailSubject.trim()
                    }
                    className="text-[10px] px-3 py-1 rounded-md border border-[#18E78F]/50 bg-[#18E78F]/10 hover:bg-[#18E78F]/20 font-medium disabled:opacity-40"
                  >
                    {emailSending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </DialogContent>
            </Dialog>
          </section>
        </div>
      </div>
    </div>
    <CircumventedDialog
      open={closingDialogOpen}
      onOpenChange={(o) => {
        if (!o && closingStep !== 'done') {
          setClosingDialogOpen(false);
          setClosingStep('confirm');
          setClosingNotes('');
          setClosingRephrased('');
        } else {
          setClosingDialogOpen(o);
        }
      }}
      step={closingStep}
      closingNotes={closingNotes}
      setClosingNotes={setClosingNotes}
      closingRephrased={closingRephrased}
      setClosingRephrased={setClosingRephrased}
      closingRephrasing={closingRephrasing}
      startRephrase={startRephraseClosing}
      proceedToNotes={() => setClosingStep('notes')}
      proceedToPreview={() => {
        if (!closingRephrased.trim()) setClosingRephrased(closingNotes.trim());
        setClosingStep('preview');
      }}
      backFromNotes={() => setClosingStep('confirm')}
      backFromPreview={() => setClosingStep('notes')}
      cancelAll={() => {
        setClosingDialogOpen(false);
        setClosingStep('confirm');
        setClosingNotes('');
        setClosingRephrased('');
      }}
      sendClosureEmail={sendClosureEmail}
      canSend={!!emailRecipients.length && !!(closingRephrased || closingNotes).trim()}
      sending={closingEmailSending}
      openClosureEmailPreview={openClosureEmailPreview}
      hasRecipients={emailRecipients.length > 0}
      currentIncident={currentIncident}
      currentImpactedApps={currentImpactedApps}
      currentComms={currentComms}
      execSummary={execSummary}
      setExecSummary={setExecSummary}
      generateExecutiveSummary={generateExecutiveSummary}
      loadingExecSummary={loadingExecSummary}
      emailRecipients={emailRecipients.map(r => r.email)} // adapt to CircumventedDialog's string[] prop
      setEmailRecipients={(next) => {
        // adapt setter from string[] to EmailRecipient[]
        setCurrentIncidentEmailRecipients(prev => {
          const resolved = typeof next === 'function'
            ? (next(prev.map(r => r.email)))
            : next;
          return resolved.map(email => ({ email }));
        });
      }}
      emailInput={emailInput}
      setEmailInput={setEmailInput}
      emailSubject={emailSubject}
      setEmailSubject={setEmailSubject}
      emailSending={emailSending}
      emailSent={emailSent}
      setEmailSent={setEmailSent}
      emailHtml={emailHtml}
      setEmailHtml={setEmailHtml}
      setPreviewOpen={setPreviewOpen}
      userRole={userRole} // <-- pass user role to enable approval flow,
      userEmail={userEmail}
      onApprovalSubmitted={(c: Communication) => {
        
        setCommunications(prev => {
          const index = prev.findIndex(x => x.update_id === c.update_id);
          
          if (index >= 0) {
            // Update existing entry (merge to preserve any fields)
            const updated = [...prev];
            updated[index] = { ...updated[index], ...c };
            return updated;
          } else {
            // Add new entry
            return [...prev, c];
          }
        });
        
        // ✅ NEW: If circumvented, mark incident as circumvented and close updates
        if (c.iscircumvented) {
          setClosedUpdates(prev => new Set([...prev, c.incidentId]));
          setIncidents(prev => prev.map(i =>
            i.id === c.incidentId 
              ? { ...i, circumvented: true, state: 'circumvented' } 
              : i
          ));
        }
        
        // ✅ Regenerate summaries immediately (no setTimeout needed)
        regenSummary();
        generateExecutiveSummary();
      }}
      onIncidentCircumvented={(incidentId: string) => {
        // ✅ Mark incident as circumvented and close updates
        setClosedUpdates(prev => new Set([...prev, incidentId]));
        setIncidents(prev => prev.map(i =>
          i.id === incidentId 
            ? { ...i, circumvented: true, state: 'circumvented' } 
            : i
        ));
      }}
    />

    <Dialog open={closurePreviewOpen} onOpenChange={setClosurePreviewOpen}>
      <DialogContent className="max-w-4xl w-[92vw] h-[80vh] p-4 flex flex-col">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-sm">
            Circumvented Email Preview {currentIncident ? `– ${currentIncident.id}` : ''}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 rounded-md border border-border/50 bg-background/60 flex">
          {closureEmailHtml ? (
            <EmailIframePreview html={closureEmailHtml} full />
          ) : (
            <div className="m-auto text-xs text-muted-foreground">Building preview…</div>
          )}
        </div>
        <div className="pt-3 flex justify-end gap-2">
          {/* <button
            onClick={() => navigator.clipboard.writeText(closureEmailHtml).catch(() => {})}
            className="text-[10px] px-3 py-1 rounded-md border border-border/50 hover:bg-background/60"
          >
            Copy HTML
          </button> */}
          <button
            onClick={sendClosureEmail}
            disabled={
              closingEmailSending ||
              !emailRecipients.length ||
              !(closingRephrased || closingNotes).trim()
            }
            className="text-[10px] px-3 py-1 rounded-md border border-[#18E78F]/50 bg-[#18E78F]/10 hover:bg-[#18E78F]/20 font-medium disabled:opacity-40"
          >
            {closingEmailSending ? 'Sending…' : 'Send Final Email'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
    <Dialog open={knowledgeArticleOpen} onOpenChange={setKnowledgeArticleOpen}>
      <DialogContent className="max-w-3xl w-[92vw] p-4 flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle className="text-sm">
            Knowledge Article – {currentIncident?.id}
          </DialogTitle>
        </DialogHeader>
        {currentIncident ? (
          <KnowledgeArticle
            incident={currentIncident}
            communications={currentComms}
            closingNotes={
              currentComms
                .filter(c => c.iscircumvented)
                .map(c => c.content)
                .join('\n\n') || ''
            }
            onClose={() => setKnowledgeArticleOpen(false)}
            onSaved={(url) => {
              if (!currentIncident) return;
              if (!url) return;
              setIncidents(prev =>
                prev.map(i =>
                  i.id === currentIncident.id
                    ? { ...i, ka_url: url, ka_generated_at: new Date().toISOString() }
                    : i
                )
              );
            }}
          />
        ) : (
          <div className="text-xs text-muted-foreground">No incident selected.</div>
        )}
      </DialogContent>
    </Dialog>
    </></Page>
  );
}
function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col bg-background/50 dark:bg-background/40 border border-border/40 rounded-lg px-2 py-1.5">
      <span className="text-[9px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-[11px] font-semibold">{value}</span>
    </div>
  );
}


function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="block text-[9px] uppercase tracking-wide text-muted-foreground/70">
        {label}
      </span>
      <span className="text-[10px] font-medium whitespace-pre-wrap">
        {value}
      </span>
    </div>
  );
}

function EditableArea({
  label,
  value,
  onChange,
  singleLine = false,
  placeholder,
  isDateTime = false,
  disabled = false // ✅ NEW prop
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  singleLine?: boolean;
  placeholder?: string;
  isDateTime?: boolean;
  disabled?: boolean; // ✅ NEW type
}) {
  const parseDateTime = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      return new Date(+year, +month - 1, +day, +hour, +minute, +second);
    }
    
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatDateTime = (date: Date): string => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  if (isDateTime) {
    const selectedDate = parseDateTime(value);
    
    const currentTime = selectedDate 
      ? `${String(selectedDate.getHours()).padStart(2, '0')}:${String(selectedDate.getMinutes()).padStart(2, '0')}`
      : '';
    
    return (
      <div className="flex flex-col gap-1">
        <label className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </label>
        
        {/* Date + Time Picker */}
        <div className="grid grid-cols-2 gap-2">
          {/* Date Picker */}
          <div className="relative">
            <DatePicker
              selected={selectedDate}
              onChange={(date: Date | null) => {
                if (disabled) return; // ✅ Prevent changes when disabled
                if (date) {
                  if (selectedDate) {
                    date.setHours(
                      selectedDate.getHours(),
                      selectedDate.getMinutes(),
                      selectedDate.getSeconds()
                    );
                  } else {
                    const now = new Date();
                    date.setHours(now.getHours(), now.getMinutes(), 0);
                  }
                  onChange(formatDateTime(date));
                } else {
                  onChange('');
                }
              }}
              dateFormat="yyyy-MM-dd"
              placeholderText={placeholder || "Select date..."}
              isClearable={!disabled} // ✅ Hide clear button when disabled
              disabled={disabled} // ✅ Disable picker
              popperPlacement="bottom-start"
              popperModifiers={[
                {
                  name: 'offset',
                  options: {
                    offset: [0, 4],
                  },
                  fn: (state: any) => state,
                },
                {
                  name: 'preventOverflow',
                  options: {
                    rootBoundary: 'viewport',
                    tether: false,
                    altAxis: true,
                  },
                  fn: (state: any) => state,
                },
              ]}
              className="text-[10px] px-2 py-1 rounded-md bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50 w-full disabled:opacity-50 disabled:cursor-not-allowed"
              calendarClassName="text-xs"
              wrapperClassName="w-full"
            />
          </div>
          
          {/* Time Input (HH:MM) */}
          <input
            type="time"
            value={currentTime}
            onChange={(e) => {
              if (disabled || !e.target.value) return;
              
              const [hours, minutes] = e.target.value.split(':').map(Number);
              
              // Use the currently selected date as the base, or today if none
              const baseDate = selectedDate ? new Date(selectedDate) : new Date();
              baseDate.setHours(hours, minutes, 0, 0);
              
              onChange(formatDateTime(baseDate));
            }}
            disabled={disabled || !selectedDate} // ✅ Also respect disabled prop
            className="text-[10px] px-2 py-1 rounded-md bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50 disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder="HH:MM"
          />
        </div>
        
        <span className="text-[8px] text-muted-foreground/60">
          {disabled 
            ? 'This field is only editable after the incident is circumvented'
            : selectedDate 
              ? `Selected: ${formatDateTime(selectedDate)} CST` 
              : 'Leave empty to auto-set on circumvention'}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <label className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {singleLine ? (
        <input
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled} // ✅ Support disabled for regular inputs
          className="text-[10px] px-2 py-1 rounded-md bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50 disabled:opacity-50 disabled:cursor-not-allowed"
          placeholder={placeholder || label}
        />
      ) : (
        <textarea
          rows={3}
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled} // ✅ Support disabled for textareas
          className="text-[10px] resize-none px-2 py-1.5 rounded-md bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50 custom-scrollbar incidents-scroll disabled:opacity-50 disabled:cursor-not-allowed"
          placeholder={placeholder || label}
        />
      )}
    </div>
  );
}